import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { track } from '@vercel/analytics';

import { useStep } from '@/hooks/useStep';
import {
  getJson,
  apiFetch,
  postJson,
  ENV,
  BASE_URL,
  EMAIL_COMERCIAL_DEV,
} from '@/lib/apiConfig';
import {
  fetchCartByPedido,
  fetchCartItemsByCart,
  finalizarPedido,
  createCart,
  addCartItem,
} from '@/services/cart';
import {
  BudgetEmailDeliveryError,
  isOpportunityEmailFailure,
  sendBudgetConfirmationEmail,
} from '@/services/budgetConfirmationEmail';
import { submitCsatResponse } from '@/services/csat';
import { formatCurrency, roundMoney } from '@/utils/helpers';
import { userFacingError } from '@/utils/translateApiMessage';
import {
  formatNomeProdutoIndisponivel,
  isGenericProdutoLabel,
} from '@/utils/duplicacaoPrecos';
import {
  collectStoredProductNames,
  eventItemsHaveCoffee,
  isUtensiliosItemLockedByCoffee,
  readPedidoNamesSnapshot,
  UTENSILIOS_LOCK_REASON,
} from '@/utils/utensiliosLock';
import {
  carolinoTrackingFromEvento,
  getCarolinoTrackingFields,
} from '@/utils/getCarolinoTracking';
import { clearCopeiraOverride } from '@/utils/getCopeiraOverride';
import {
  clearDuplicacaoEdicaoFormLocalStorage,
  STORAGE_KEYS,
} from '@/utils/storage';
import {
  logPedidoFinalizacaoRow,
  maskEmail,
  PedidoFinalizeSteps,
  httpStatusFromError,
  safeErrorDetail,
  type PedidoFinalizePdfLogCtx,
} from '@/services/orderFinalizeLog';

import './style.css';
import { jsPDF } from 'jspdf';
import { CheckoutSuccessPanel } from '@/shared/CheckoutSuccessPanel';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  hideCloseButton?: boolean; // Nova prop para controlar visibilidade do botão X
  previewFinalSuccess?: boolean;
}

const parseCurrencyValue = (value: any): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value
      .replace(/\s/g, '')
      .replace(/[^\d,.-]/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const coerced = Number(value);
  return Number.isFinite(coerced) ? coerced : 0;
};

const getFreteValue = (record: any): number => {
  if (record == null) {
    return 0;
  }

  if (typeof record !== 'object') {
    return parseCurrencyValue(record);
  }

  const hasProp = (prop: string) =>
    Object.prototype.hasOwnProperty.call(record, prop);

  if (hasProp('shipping')) {
    const shippingValue = record.shipping;
    if (shippingValue !== '' && shippingValue !== undefined && shippingValue !== null) {
      return parseCurrencyValue(shippingValue);
    }
  }

  const fallbackKeys = ['frete', 'freight', 'valorFrete', 'valor_frete'];
  for (const key of fallbackKeys) {
    if (hasProp(key)) {
      const value = record[key];
      if (value !== undefined && value !== null && value !== '') {
        return parseCurrencyValue(value);
      }
    }
  }

  return 0;
};

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  hideCloseButton = false,
  previewFinalSuccess = false,
}) => {
  const navigate = useNavigate();
  const { step, setStep } = useStep(7);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [freteTotal, setFreteTotal] = useState<number>(0);
  const [servicosTotal, setServicosTotal] = useState<number>(0);
  const [produtosTotal, setProdutosTotal] = useState<number>(0);
  const [pedidoId, setPedidoId] = useState<number>(0);
  const [carrinhoId, setCarrinhoId] = useState<number>(0);
  const [productNamesById, setProductNamesById] = useState<
    Record<number, string>
  >({});
  const [serviceNamesById, setServiceNamesById] = useState<
    Record<number, string>
  >({});
  const fetchedProductNamesRef = useRef<Set<number>>(new Set());
  const [expandedPedidos, setExpandedPedidos] = useState<Set<number>>(
    new Set()
  );
  const [carrinhos, setCarrinhos] = useState<any[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [confirmationEmailFailed, setConfirmationEmailFailed] =
    useState(false);
  const [csatRating, setCsatRating] = useState(0);
  const [csatFeedback, setCsatFeedback] = useState('');
  const [csatSubmitted, setCsatSubmitted] = useState(false);
  const [showCheckoutContent, setShowCheckoutContent] = useState<boolean>(true); // Novo estado para controlar visibilidade do conteúdo do checkout
  const [isFinalizing, setIsFinalizing] = useState<boolean>(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>('');
  const [loadingItems, setLoadingItems] = useState<Set<number>>(new Set());
  const [deletingItems, setDeletingItems] = useState<Set<number>>(new Set()); // Estado separado para itens sendo deletados
  const [isPaymentExpanded, setIsPaymentExpanded] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState<boolean>(false);

  // Estado para controlar o pop-up de confirmação de exclusão
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] =
    useState<boolean>(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);

  // Cache para dados do usuário (evita múltiplas chamadas auth/me)
  const [userDataCache, setUserDataCache] = useState<any>(null);
  const [isLoadingCart, setIsLoadingCart] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);

  // Cache para status dos produtos (Ativo, Visivel_Cliente, Disponivel_Venda)
  const [productDetailsCache, setProductDetailsCache] = useState<
    Record<number, any>
  >({});

  // Ref para rastrear se o pedido foi finalizado (para analytics)
  const pedidoFinalizadoRef = useRef<boolean>(false);
  /** Correlaciona todos os logs de uma mesma tentativa de finalização (audit trail no XANO). */
  const finalizeCorrelationIdRef = useRef<string>('');
  // Ref para rastrear se o checkout foi aberto (para analytics)
  const checkoutAbertoRef = useRef<boolean>(false);
  // Refs para capturar valores no momento do beforeunload
  const pedidoIdRef = useRef<number>(0);
  /** Mantém o último pedido finalizado para "Gerar PDF" no modal de sucesso, após LS/state serem limpos. */
  const ultimoPedidoFinalizadoParaPdfRef = useRef<number>(0);
  const itemsRef = useRef<any[]>([]);
  const totalRef = useRef<number>(0);
  const userEmailRef = useRef<string | null>(null);

  // Função wrapper para onClose que faz tracking antes de fechar
  const handleClose = useCallback(() => {
    // Tracking: usuário fechou checkout sem confirmar (se não foi finalizado)
    if (checkoutAbertoRef.current && !pedidoFinalizadoRef.current) {
      // Buscar pedido_id do localStorage caso ainda não esteja disponível
      const pedidoIdValue = pedidoId || 
        (localStorage.getItem('pedido_id') ? parseInt(localStorage.getItem('pedido_id') || '0') : 0);
      
      // Buscar email do cliente (do localStorage ou do cache)
      const userEmail = localStorage.getItem('userEmail') || userDataCache?.email || null;
      
      const trackingData: Record<string, string | number> = {
        total_items: items.length,
        valor_total: total,
      };
      if (pedidoIdValue && pedidoIdValue > 0) {
        trackingData.pedido_id = pedidoIdValue;
      }
      if (userEmail) {
        trackingData.email_cliente = userEmail;
      }
      track('Checkout Fechado Sem Confirmar', trackingData);
    }
    checkoutAbertoRef.current = false;
    onClose();
  }, [onClose, pedidoId, items.length, total, userDataCache]);

  // Limpeza segura quando não há itens: mantém login e pedido_id
  const clearStateOnEmptyCart = useCallback(() => {
    // manter auth e pedido_id
    const keysToKeep = ['user_id', 'authToken', 'userEmail', 'pedido_id'];
    Object.keys(localStorage).forEach(key => {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    setItems([]);
    setCarrinhos([]);
    setProdutosTotal(0);
    setServicosTotal(0);
    setFreteTotal(0);
    setTotal(0);
    setSelectedPaymentMethod('');
    setShowCheckoutContent(false);

    setStep(1);
    handleClose();
    navigate('/');
  }, [navigate, handleClose, setStep]);
  
  // Verifica se um produto está disponível com base no cache de status
  const isProductAvailable = (item: any): boolean => {
    if (!item?.produto_id) return true; // serviços ou itens sem produto_id ficam sempre disponíveis

    if (item.disponivel === false) return false;

    const status = productDetailsCache[item.produto_id];
    if (!status) return true; // se ainda não carregamos status, não bloqueia

    const toBool = (value: any): boolean =>
      value === true ||
      value === 'Sim' ||
      value === 'sim' ||
      value === 1 ||
      value === '1';

    const hasAtivo = Object.prototype.hasOwnProperty.call(status, 'Ativo');
    const hasVisivel = Object.prototype.hasOwnProperty.call(
      status,
      'Visivel_Cliente'
    );
    const hasDisp = Object.prototype.hasOwnProperty.call(
      status,
      'Disponivel_Venda'
    );

    // Se não temos nenhuma flag, não bloqueia
    if (!hasAtivo && !hasVisivel && !hasDisp) return true;

    const ativoOk = hasAtivo ? toBool(status.Ativo) : true;
    const visivelOk = hasVisivel ? toBool(status.Visivel_Cliente) : true;
    const dispOk = hasDisp ? toBool(status.Disponivel_Venda) : true;

    // Se QUALQUER um estiver falso, produto indisponível
    return ativoOk && visivelOk && dispOk;
  };

  // Botão de "Sort" para produtos indisponíveis: tenta buscar um produto da mesma família
  // usando o endpoint de sugestão (produtoFamilia) e substitui apenas no front.
  const handleRandomReplaceUnavailableItem = async (item: any) => {
    try {
      if (!item?.produto_id) return;

      // Buscar detalhes do produto atual para descobrir a família
      const respostaDetalhe: any = await getJson(
        'rEsr7KLt',
        `produto/${item.produto_id}`
      );
      const detalheProduto: any = Array.isArray(respostaDetalhe)
        ? respostaDetalhe[0]
        : respostaDetalhe?.items && Array.isArray(respostaDetalhe.items)
        ? respostaDetalhe.items[0]
        : respostaDetalhe;

      const familia: string | undefined =
        detalheProduto?.Familia ||
        detalheProduto?.familia ||
        detalheProduto?.Sub_Familia;

      if (!familia) {
        toast.error('Produto não possui família configurada para sugestão.');
        return;
      }

      const pessoas =
        parseInt(localStorage.getItem('pessoas') || '0', 10) || 0;
      const eventTypeRaw = localStorage.getItem('eventType') || 'manha';
      const mapEventType = (et: string): string => {
        const map: any = {
          manha: 'Coffee Break - Manhã',
          tarde: 'Coffee Break - Tarde',
          almoco: 'Almoço',
          happy: 'Happy Hour',
          outros: 'Outros',
        };
        return map[(et || '').toLowerCase()] || et;
      };
      const evento = mapEventType(eventTypeRaw);

      const data: any = await getJson('rEsr7KLt', 'produtoFamilia', {
        page: 0,
        familia,
        pessoas,
        evento,
      });

      const apiItems: any[] = Array.isArray(data?.items) ? data.items : [];

      // Candidatos: não podem ser o mesmo produto e precisam estar disponíveis
      const candidatos = apiItems.filter(p => {
        const ativo =
          p.Ativo === true || p.Ativo === 'Sim' || p.Ativo === 1 || p.Ativo === '1';
        const visivel =
          p.Visivel_Cliente === true ||
          p.Visivel_Cliente === 'Sim' ||
          p.Visivel_Cliente === 1 ||
          p.Visivel_Cliente === '1';
        const disp =
          p.Disponivel_Venda === undefined ||
          p.Disponivel_Venda === null ||
          p.Disponivel_Venda === true ||
          p.Disponivel_Venda === 'Sim' ||
          p.Disponivel_Venda === 1 ||
          p.Disponivel_Venda === '1';

        return p.id !== item.produto_id && ativo && visivel && disp;
      });

      if (!candidatos.length) {
        toast.error('Não há outro produto disponível desta família para sugerir.');
        return;
      }

      const replacement =
        candidatos[Math.floor(Math.random() * candidatos.length)];

      // Calcular preço unitário do novo produto (mesma lógica do modal)
      const qtdTotal = Number(replacement.QtdTotal) || 0;
      const valorTotal = Number(replacement.ValorTotal) || 0;
      const valorUnit =
        qtdTotal > 0 && valorTotal > 0
          ? valorTotal / qtdTotal
          : Number(replacement.Valor || replacement.valor || 0);

      const currentQty = Number(item.qtd || 0);
      const unitPrice = valorUnit > 0 ? valorUnit : 0;
      const newTotal = roundMoney(unitPrice * currentQty);

      // Substituir no estado local do checkout
      const updatedItems = items.map(i =>
        i.id === item.id
          ? {
              ...i,
              produto_id: replacement.id,
              total: newTotal,
              _produto: [
                {
                  Nome: replacement.Nome || replacement.name || '',
                  Imagem: {
                    url:
                      replacement?.Imagem?.url ||
                      replacement?.image ||
                      i._produto?.[0]?.Imagem?.url ||
                      '',
                  },
                },
              ],
            }
          : i
      );

      // Recalcular totais com base na lista nova
      const produtosTotalLocal = updatedItems
        .filter((i: any) => i.produto_id && !i.servicos_id)
        .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
      const servicosTotalLocal = updatedItems
        .filter((i: any) => i.servicos_id)
        .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
      const freteTotalLocal = freteTotal; // frete não muda

      setItems(updatedItems);
      // Atualizar nome do produto no mapa de nomes (para não aparecer Produto #id)
      setProductNamesById(prev => ({
        ...prev,
        [replacement.id]:
          replacement.Nome || replacement.name || `Produto #${replacement.id}`,
      }));
      setProdutosTotal(produtosTotalLocal);
      setServicosTotal(servicosTotalLocal);
      setTotal(produtosTotalLocal + servicosTotalLocal + freteTotalLocal);

      // Atualizar cache de status para o novo produto
      setProductDetailsCache(prev => ({
        ...prev,
        [replacement.id]: {
          Visivel_Cliente: replacement.Visivel_Cliente,
          Ativo: replacement.Ativo,
          Disponivel_Venda: replacement.Disponivel_Venda,
        },
      }));

      // Se estiver em modo duplicação, persistir também em duplicacao_eventos
      if (isDuplicacao) {
        try {
          const eventosDuplicados = readDuplicacaoEventosFromStorage();
          const localIdFromCarrinho = -Number(item.carrinho_id);
          const targetIndex = eventosDuplicados.findIndex((ev: any, idx: number) => {
            const evLocalId = Number(ev?.localId || idx + 1);
            return evLocalId === localIdFromCarrinho;
          });

          if (targetIndex >= 0) {
            const eventosAtualizados = [...eventosDuplicados];
            const eventoAlvo = { ...eventosAtualizados[targetIndex] };
            const produtosOriginais = eventoAlvo.produtos || [];

            const produtosAtualizados = produtosOriginais.map((p: any) => {
              if (Number(p.produto_id) !== Number(item.produto_id)) return p;
              return {
                ...p,
                produto_id: replacement.id,
                nome: replacement.Nome || replacement.name || p.nome,
                unitPrice: unitPrice,
                price: unitPrice,
                quantity: currentQty,
                image:
                  replacement?.Imagem?.url ||
                  replacement?.image ||
                  p.image ||
                  '',
              };
            });

            eventoAlvo.produtos = produtosAtualizados;
            eventosAtualizados[targetIndex] = eventoAlvo;
            persistDuplicacaoEventos(eventosAtualizados);
          }
        } catch {
          // Se falhar a persistência, pelo menos o front já está atualizado
        }
      }

      const successToastId = toast.success('Produto substituído com sucesso!', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setTimeout(() => {
        toast.dismiss(successToastId);
      }, 2500);
    } catch (error) {
      toast.error('Erro ao buscar sugestão de produto.');
    }
  };

  // Estados para modo duplicação
  const [isDuplicacao, setIsDuplicacao] = useState<boolean>(false);
  const [eventosConfirmados, setEventosConfirmados] =
    useState<Record<number, boolean>>({});
  const [duplicacaoEventos, setDuplicacaoEventos] = useState<any[]>([]);

  // Detectar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (showSuccessModal) {
      setCsatRating(0);
      setCsatFeedback('');
      setCsatSubmitted(false);
    }
  }, [showSuccessModal]);

  // Função para atualizar estados de confirmação
  const updateConfirmationStates = () => {
    const duplicacaoAtiva = localStorage.getItem('duplicacao_ativa') === 'true';
    setIsDuplicacao(duplicacaoAtiva);

    if (duplicacaoAtiva) {
      const eventosDuplicados = readDuplicacaoEventosFromStorage();
      setDuplicacaoEventos(eventosDuplicados);
      const confirmados: Record<number, boolean> = {};
      eventosDuplicados.forEach((evento: any, idx: number) => {
        // Usar a mesma lógica de geração de ID negativo que é usada em loadCart
        // para garantir que a chave corresponda ao carrinhoId usado no render
        const localId = Number(evento?.localId || idx + 1);
        const carrinhoId = -localId; // IDs negativos para eventos duplicados
        if (Number.isFinite(carrinhoId)) {
          confirmados[carrinhoId] = !!evento.isConfirmed;
        }
      });
      setEventosConfirmados(confirmados);
    } else {
      setDuplicacaoEventos([]);
      setEventosConfirmados({});
    }
  };

  // Detectar modo duplicação e carregar estados de confirmação
  useEffect(() => {
    updateConfirmationStates();

    // Listener para atualizar quando o carrinho é atualizado (ex: evento confirmado)
    const handleCartUpdated = () => {
      updateConfirmationStates();
    };

    window.addEventListener('cart-updated', handleCartUpdated);

    return () => {
      window.removeEventListener('cart-updated', handleCartUpdated);
    };
  }, [isOpen]);

  // Helpers

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      // Se for formato YYYY-MM-DD
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const getEventTypeName = (eventType: string): string => {
    const eventTypeMap: { [key: string]: string } = {
      manha: 'Coffee Break - Manhã',
      almoco: 'Almoço',
      tarde: 'Coffee Break - Tarde',
      happy: 'Happy Hour',
      outros: 'Outros',
    };
    const normalized = eventType?.toLowerCase().trim() || '';
    return eventTypeMap[normalized] || eventType || 'Evento';
  };

  const cleanCepValue = (value: string) => (value || '').replace(/\D/g, '');

  const readDuplicacaoEventosFromStorage = (): any[] => {
    try {
      const stored = localStorage.getItem('duplicacao_eventos');
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const persistDuplicacaoEventos = (eventos: any[]) => {
    setDuplicacaoEventos(eventos);
    try {
      localStorage.setItem('duplicacao_eventos', JSON.stringify(eventos));
      if (eventos.length === 0) {
        localStorage.removeItem('duplicacao_ativa');
        setIsDuplicacao(false);
        setEventosConfirmados({});
      }
    } catch {
      // ignorar erro de persistência
    }
  };

  const convertDuplicacaoProdutosToSelected = (produtos: any[] = []) => {
    return produtos.map(produto => {
      const produtoId = Number(produto?.produto_id || produto?.id);
      const nomeRaw = produto?.nome || produto?.name || '';
      const isUnavailable =
        produto?.disponivel === false ||
        isGenericProdutoLabel(nomeRaw, produtoId);
      return {
        id: String(produto?.produto_id || produto?.id || Math.random()),
        name: isUnavailable
          ? formatNomeProdutoIndisponivel(nomeRaw, produtoId)
          : nomeRaw || `Produto ${produtoId}`,
        price: Number(produto?.unitPrice || produto?.price || 0),
        quantity: Number(produto?.quantity || 0),
        image: produto?.image || '',
        familia: produto?.familia || '',
        isUnavailable,
      };
    });
  };

  const convertDuplicacaoServicosToAdditional = (evento: any) => {
    const servicos = evento?.servicos || [];
    const copeira = servicos.find(
      (servico: any) => Number(servico?.servico_id) === 2
    );
    const utensilios = servicos.find(
      (servico: any) => Number(servico?.servico_id) === 4
    );

    return {
      flags: {
        userEditedCopeiraQty: !!copeira,
      },
      copeira: {
        selected: !!copeira,
        quantity: Number(copeira?.quantity || 0),
        serviceId: copeira ? String(copeira.servico_id) : undefined,
        priceCents: copeira
          ? Math.round(
              Number(copeira.unitPrice || copeira.price || 0) * 100
            )
          : undefined,
      },
      utensilios: {
        selected: !!utensilios,
        lockedByCoffee: false,
        serviceId: utensilios ? String(utensilios.servico_id) : undefined,
        priceCents: utensilios
          ? Math.round(
              Number(utensilios.unitPrice || utensilios.price || 0) * 100
            )
          : undefined,
      },
      observacao: evento?.obs_servicos || '',
      distanciaKm: evento?.km ? Number(evento.km) : undefined,
    };
  };

  // Funções para controlar sanfonas
  const togglePedido = (pedidoId: number) => {
    const newExpanded = new Set(expandedPedidos);
    if (newExpanded.has(pedidoId)) {
      newExpanded.delete(pedidoId);
    } else {
      newExpanded.add(pedidoId);
    }
    setExpandedPedidos(newExpanded);
  };

  // Leitura dos dados salvos
  const resumo = useMemo(() => {
    const finalDateTime = localStorage.getItem('finalDateTime') || '';
    const finalNotes = localStorage.getItem('finalNotes') || '';
    const pessoas = parseInt(localStorage.getItem('pessoas') || '0') || 0;
    const eventType = localStorage.getItem('eventType') || 'manha';
    const eventTypeName = getEventTypeName(eventType);
    const cep = localStorage.getItem('cep') || '';
    const distancia = localStorage.getItem('distancia') || '';
    let enderecoData: any = {};
    try {
      enderecoData = JSON.parse(localStorage.getItem('enderecoData') || '{}');
    } catch {}
    let additionalServices: any = {};
    try {
      additionalServices = JSON.parse(
        localStorage.getItem('additionalServices') || '{}'
      );
    } catch {}

    const copeiraQty = Number(additionalServices?.copeira?.quantity || 0);
    const copeiraPrice = Number(
      (additionalServices?.copeira?.priceCents || 0) / 100
    );
    const copeiraTotal = copeiraQty > 0 ? copeiraQty * copeiraPrice : 0;
    const utensiliosSelected = !!additionalServices?.utensilios?.selected;
    const utensiliosPrice = Number(
      (additionalServices?.utensilios?.priceCents || 0) / 100
    );

    return {
      finalDateTime,
      finalNotes,
      pessoas,
      eventTypeName,
      cep,
      distancia,
      enderecoData,
      services: {
        copeiraQty,
        copeiraPrice,
        copeiraTotal,
        utensiliosSelected,
        utensiliosPrice,
      },
    };
  }, []);

  // Fechar modal com tecla ESC e prevenir scroll do body
  useEffect(() => {
    if (isOpen) {
      // Prevenir scroll do body quando modal está aberto
      document.body.style.overflow = 'hidden';

      if (previewFinalSuccess) {
        // Modo preview: abre direto no modal final sem depender do fluxo de checkout
        setShowCheckoutContent(false);
        setShowSuccessModal(true);
        setConfirmationEmailFailed(
          new URLSearchParams(window.location.search).get('email') === 'falhou'
        );
        pedidoFinalizadoRef.current = true;
        checkoutAbertoRef.current = false;

        return () => {
          document.body.style.overflow = 'unset';
        };
      }

      // Resetar estado do checkout quando modal abre
      setShowCheckoutContent(true);
      setShowSuccessModal(false);
      
      // Resetar flag de finalização quando checkout abre
      pedidoFinalizadoRef.current = false;
      checkoutAbertoRef.current = true;

      // Buscar pedido_id do localStorage caso ainda não esteja disponível
      const pedidoIdParaTracking = pedidoId || 
        (localStorage.getItem('pedido_id') ? parseInt(localStorage.getItem('pedido_id') || '0') : 0);

      // Tracking: usuário chegou na última tela (Checkout)
      const trackingData: Record<string, string | number> = {};
      if (pedidoIdParaTracking) {
        trackingData.pedido_id = pedidoIdParaTracking;
      }
      track('Checkout Aberto', trackingData);

      // Limpar campos de data/hora e endereço ao abrir o carrinho
      localStorage.removeItem('finalDateTime');
      localStorage.removeItem('finalNotes');
      localStorage.removeItem('cep');
      localStorage.removeItem('cepData');
      localStorage.removeItem('enderecoData');
      localStorage.removeItem('distancia');

      // Refs serão atualizados pelo useEffect dedicado abaixo

      // Listener para capturar quando o usuário fecha a página/aba sem confirmar
      const handleBeforeUnload = () => {
        if (checkoutAbertoRef.current && !pedidoFinalizadoRef.current) {
          const pedidoIdValue = pedidoIdRef.current || 
            (localStorage.getItem('pedido_id') ? parseInt(localStorage.getItem('pedido_id') || '0') : 0);
          
          // Buscar email do cliente (do ref ou localStorage)
          const userEmail = userEmailRef.current || localStorage.getItem('userEmail') || null;
          
          // Tentar enviar evento antes da página fechar
          try {
            const trackingData: Record<string, string | number> = {
              total_items: itemsRef.current.length,
              valor_total: totalRef.current,
              motivo: 'pagina_fechada',
            };
            if (pedidoIdValue && pedidoIdValue > 0) {
              trackingData.pedido_id = pedidoIdValue;
            }
            if (userEmail) {
              trackingData.email_cliente = userEmail;
            }
            track('Checkout Fechado Sem Confirmar', trackingData);
          } catch (e) {
            console.error('Erro ao enviar tracking ao fechar página:', e);
          }
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      // Não permitir fechar o carrinho com ESC para evitar voltar para tela de info finais
      // O carrinho só pode ser fechado através dos botões dentro dele ou após finalizar pedido

      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isOpen, onClose, pedidoId, items.length, total, userDataCache, previewFinalSuccess]);

  // Atualizar refs sempre que valores mudarem (para beforeunload)
  useEffect(() => {
    pedidoIdRef.current = pedidoId || 
      (localStorage.getItem('pedido_id') ? parseInt(localStorage.getItem('pedido_id') || '0') : 0);
    itemsRef.current = items;
    totalRef.current = total;
    userEmailRef.current = localStorage.getItem('userEmail') || userDataCache?.email || null;
  }, [pedidoId, items, total, userDataCache]);

  // Função para buscar dados do usuário com cache
  const fetchUserData = async (forceRefresh = false): Promise<any> => {
    if (userDataCache && !forceRefresh) {
      return userDataCache;
    }

    try {
      const userData = await getJson('9-ms6pAp', 'auth/me');
      setUserDataCache(userData);
      return userData;
    } catch (e) {
      // Usuário não logado
      return null;
    }
  };

  // Flag para evitar múltiplas chamadas simultâneas
  const isLoadingCartRef = React.useRef(false);

  // Função para carregar carrinho (com debounce e otimização)
  const loadCart = async (skipLoadingState = false) => {
    // Evitar múltiplas chamadas simultâneas
    if (isLoadingCartRef.current) {
      return;
    }
    
    isLoadingCartRef.current = true;
    
    if (!skipLoadingState) {
      setIsLoadingCart(true);
    }

    const duplicacaoAtiva = localStorage.getItem('duplicacao_ativa') === 'true';
    
    // Carregar eventos duplicados do localStorage (se houver)
    let eventosDuplicados: any[] = [];
    let carrinhosVirtuais: any[] = [];
    let itensVirtuais: any[] = [];
    let produtosTotalDup = 0;
    let servicosTotalDup = 0;
    let freteTotalDup = 0;
    let allProductNamesDup: Record<number, string> = {};
    let allServiceNamesDup: Record<number, string> = {};
    
    if (duplicacaoAtiva) {
      try {
        eventosDuplicados = readDuplicacaoEventosFromStorage();
        persistDuplicacaoEventos(eventosDuplicados);

        // Mapear eventos para carrinhos e itens "virtuais"
        // Usar IDs negativos para não conflitar com IDs reais do backend
        carrinhosVirtuais = eventosDuplicados.map(
          (evento: any, index: number) => ({
            id: -(Number(evento?.localId || index + 1)), // IDs negativos para eventos duplicados
            _duplicacaoId: evento?.id,
            pessoas: evento?.pessoas || 0,
            evento: evento?.evento || '',
            data: evento?.dataFinal || evento?.data || '',
            hora: evento?.horaFinal || evento?.hora || '',
            CEP: evento?.cep || evento?.cep_logistica || '',
            logradouro: evento?.endereco?.logradouro || '',
            numero: evento?.endereco?.numero || '',
            bairro: evento?.endereco?.bairro || '',
            cidade: evento?.endereco?.cidade || '',
            UF: evento?.endereco?.uf || '',
            complemento: evento?.endereco?.complemento || '',
            obs: evento?.obs_servicos || '',
            obs_logistica: evento?.obs_logistica || '',
            frete: getFreteValue(evento),
            itens: [...(evento?.produtos || []), ...(evento?.servicos || [])],
          })
        );

        itensVirtuais = carrinhosVirtuais.flatMap(
          (carrinhoVirtual: any, idx: number) => {
            const eventoOriginal = eventosDuplicados[idx];
            const carrinhoId = carrinhoVirtual.id;

            const produtosItens =
              (eventoOriginal?.produtos || []).map((produto: any, pIdx: number) => {
                const quantity = Number(produto.quantity || 0);
                const unitPrice = Number(produto.unitPrice || produto.price || 0);
                const produtoId = Number(produto.produto_id);
                const nomeRaw = produto.nome || '';
                const indisponivel =
                  produto.disponivel === false ||
                  isGenericProdutoLabel(nomeRaw, produtoId);
                const nomeExibicao = indisponivel
                  ? formatNomeProdutoIndisponivel(nomeRaw, produtoId)
                  : nomeRaw || `Produto #${produtoId}`;
                return {
                  id: `dup-prod-${carrinhoId}-${produto.produto_id || pIdx}`,
                  produto_id: produto.produto_id,
                  carrinho_id: carrinhoId,
                  pedido_id: 0,
                  qtd: quantity,
                  total: roundMoney(unitPrice * quantity),
                  disponivel: !indisponivel,
                  _produto: [
                    {
                      Nome: nomeExibicao,
                      Imagem: { url: produto.image || '' },
                    },
                  ],
                };
              }) || [];

            const servicosItens =
              (eventoOriginal?.servicos || []).map((servico: any, sIdx: number) => {
                const quantity = Number(servico.quantity || 0);
                const unitPrice = Number(servico.unitPrice || servico.price || 0);
                const serviceName = servico.nome || 
                  (servico.servico_id === 2 || servico.servico_id === 3
                    ? 'Copeira'
                    : servico.servico_id === 4 || servico.servico_id === 5
                    ? 'Utensílios'
                    : `Serviço ${servico.servico_id}`);
                return {
                  id: `dup-serv-${carrinhoId}-${servico.servico_id || sIdx}`,
                  servicos_id: servico.servico_id,
                  carrinho_id: carrinhoId,
                  pedido_id: 0,
                  qtd: quantity,
                  total: roundMoney(unitPrice * quantity),
                  _servico: {
                    Nome: serviceName,
                  },
                };
              }) || [];

            return [...produtosItens, ...servicosItens];
          }
        );

        produtosTotalDup = eventosDuplicados.reduce(
          (sum: number, evento: any) =>
            sum +
            (evento?.produtos || []).reduce(
              (subtotal: number, produto: any) =>
                subtotal +
                Number(produto.unitPrice || produto.price || 0) *
                  Number(produto.quantity || 0),
              0
            ),
          0
        );

        servicosTotalDup = eventosDuplicados.reduce(
          (sum: number, evento: any) =>
            sum +
            (evento?.servicos || []).reduce(
              (subtotal: number, servico: any) =>
                subtotal +
                Number(servico.unitPrice || servico.price || 0) *
                  Number(servico.quantity || 0),
              0
            ),
          0
        );

        freteTotalDup = eventosDuplicados.reduce(
          (sum: number, evento: any) => sum + getFreteValue(evento),
          0
        );

        // Extrair nomes dos produtos e serviços dos itens virtuais
        itensVirtuais.forEach((item: any) => {
          // Produtos
          if (item.produto_id && item._produto && item._produto[0] && item._produto[0].Nome) {
            allProductNamesDup[item.produto_id] = item._produto[0].Nome;
          }
          
          // Serviços
          if (item.servicos_id && item.servicos_id > 0) {
            let serviceName = '';
            
            // Tentar obter do item virtual (_servico)
            if (item._servico && item._servico.Nome) {
              serviceName = item._servico.Nome;
            } else {
              // Buscar nome do serviço no evento original
              const eventoOriginal = eventosDuplicados.find((ev: any) => {
                const servico = (ev?.servicos || []).find((s: any) => s.servico_id === item.servicos_id);
                return !!servico;
              });
              
              if (eventoOriginal) {
                const servico = (eventoOriginal.servicos || []).find((s: any) => s.servico_id === item.servicos_id);
                serviceName = servico?.nome || '';
              }
              
              // Se não encontrou, usar nomes padrão
              if (!serviceName) {
                serviceName =
                  localStorage.getItem(`serviceName_${item.servicos_id}`) ||
                  (item.servicos_id === 2
                    ? 'Copeira'
                    : item.servicos_id === 3
                    ? 'Copeira'
                    : item.servicos_id === 4
                    ? 'Utensílios'
                    : item.servicos_id === 5
                    ? 'Utensílios'
                    : `Serviço ${item.servicos_id}`);
              }
            }
            
            allServiceNamesDup[item.servicos_id] = serviceName;
          }
        });
      } catch (e) {
        // Ignorar erros ao carregar eventos duplicados
      }
    }

    try {
      const pedidoId = parseInt(localStorage.getItem('pedido_id') || '0');
  
      if (!pedidoId || pedidoId <= 0) {
        // Se não há pedido_id, mas há eventos duplicados, mostrar apenas eles
        if (duplicacaoAtiva && carrinhosVirtuais.length > 0) {
          // Buscar status de todos os produtos deste evento de duplicação
          try {
            const produtosIdsDup = itensVirtuais
              .filter((item: any) => item.produto_id)
              .map((item: any) => item.produto_id)
              .filter(
                (id: any, index: number, self: any[]) =>
                  self.indexOf(id) === index
              );

            if (produtosIdsDup.length > 0) {
              const produtosDetalhesDup = await Promise.all(
                produtosIdsDup.map(async (produtoId: number) => {
                  try {
                    const produto = await getJson(
                      'rEsr7KLt',
                      `produto/${produtoId}`
                    );
                    return produto ? { id: produtoId, ...produto } : null;
                  } catch {
                    return null;
                  }
                })
              );

              const newCacheDup: Record<number, any> = {};
              produtosDetalhesDup.forEach((produto: any) => {
                if (produto && produto.id) {
                  newCacheDup[produto.id] = {
                    Visivel_Cliente:
                      produto.Visivel_Cliente ?? produto.model?.Visivel_Cliente,
                    Ativo: produto.Ativo ?? produto.model?.Ativo,
                    Disponivel_Venda:
                      produto.Disponivel_Venda ?? produto.model?.Disponivel_Venda,
                  };
                }
              });

              if (Object.keys(newCacheDup).length > 0) {
                setProductDetailsCache(prev => ({ ...prev, ...newCacheDup }));
              }
            }
          } catch {
            // Se der erro na checagem de status, não bloqueamos o carrinho
          }

          setCarrinhos(carrinhosVirtuais);
          setItems(itensVirtuais);
          setPedidoId(0);
          setCarrinhoId(0);
          setProdutosTotal(produtosTotalDup);
          setServicosTotal(servicosTotalDup);
          setFreteTotal(freteTotalDup);
          setTotal(produtosTotalDup + servicosTotalDup + freteTotalDup);
          setProductNamesById({
            ...collectStoredProductNames(),
            ...allProductNamesDup,
          });
          setServiceNamesById(allServiceNamesDup);
        } else {
          setCarrinhos([]);
          setItems([]);
          setProdutosTotal(0);
          setServicosTotal(0);
          setFreteTotal(0);
          setTotal(0);
          setPedidoId(0);
        }
        return;
      }

      setPedidoId(pedidoId);

      // PASSO 1: Buscar TODOS os carrinhos (eventos) deste pedido
      const carrinhos = await getJson('rEsr7KLt', 'carrinho', {
        pedido_id: pedidoId,
      });

      if (!Array.isArray(carrinhos) || carrinhos.length === 0) {
        // Se não há carrinhos no backend, mas há eventos duplicados, mostrar apenas eles
        if (duplicacaoAtiva && carrinhosVirtuais.length > 0) {
          setCarrinhos(carrinhosVirtuais);
          setItems(itensVirtuais);
          setProdutosTotal(produtosTotalDup);
          setServicosTotal(servicosTotalDup);
          setFreteTotal(freteTotalDup);
          setTotal(produtosTotalDup + servicosTotalDup + freteTotalDup);
          setProductNamesById({
            ...collectStoredProductNames(),
            ...allProductNamesDup,
          });
          setServiceNamesById(allServiceNamesDup);
        } else {
          clearStateOnEmptyCart();
        }
        return;
      }

      // PASSO 2: Buscar itens de TODOS os carrinhos em paralelo (Promise.all)
      const itensPorCarrinho = await Promise.all(
        carrinhos.map(async (carrinho: any) => {
          try {
            const items = await getJson('rEsr7KLt', 'carrinho_produtos', {
              carrinho_id: carrinho.id,
              pedido_id: pedidoId,
            });
            const sorted = Array.isArray(items)
              ? [...items].sort((a: any, b: any) => Number(a.id) - Number(b.id))
              : [];
            return {
              carrinhoId: carrinho.id,
              carrinho: carrinho,
              itens: sorted,
            };
          } catch (error) {
            return { carrinhoId: carrinho.id, carrinho: carrinho, itens: [] };
          }
        })
      );

      // Consolidar TODOS os itens em um único array
      const allItems: any[] = itensPorCarrinho.flatMap(x => x.itens);

      // Mapa por carrinho_id para renderização
      const itensByCarrinho: Record<number, any[]> = itensPorCarrinho.reduce(
        (acc, { carrinhoId, itens }) => {
          acc[carrinhoId] = itens;
          return acc;
        },
        {} as Record<number, any[]>
      );

      if (allItems.length === 0) {
        // Se não há itens no backend, mas há eventos duplicados, combinar
        if (duplicacaoAtiva && carrinhosVirtuais.length > 0) {
          const todosCarrinhos = [...carrinhosVirtuais, ...carrinhos];
          setCarrinhos(todosCarrinhos);
          setItems(itensVirtuais);
          setProdutosTotal(produtosTotalDup);
          setServicosTotal(servicosTotalDup);
          setFreteTotal(freteTotalDup);
          setTotal(produtosTotalDup + servicosTotalDup + freteTotalDup);
          setProductNamesById({
            ...collectStoredProductNames(),
            ...allProductNamesDup,
          });
          setServiceNamesById(allServiceNamesDup);
        } else {
          clearStateOnEmptyCart();
        }
        return;
      }

      // PASSO 3: Calcular totais por evento (carrinho)
      const eventos = carrinhos.map((carrinho: any) => {
        const itens = itensByCarrinho[carrinho.id] || [];
        const subtotalProdutos = itens
          .filter((i: any) => i.produto_id && !i.servicos_id)
          .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
        const subtotalServicos = itens
          .filter((i: any) => i.servicos_id)
          .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
        const subtotalFrete = getFreteValue(carrinho);
        const subtotal = subtotalProdutos + subtotalServicos + subtotalFrete;

        return {
          carrinho,
          itens,
          subtotal,
          subtotalProdutos,
          subtotalServicos,
          subtotalFrete,
        };
      });

      // PASSO 4: Calcular resumo do pedido (soma de todos os eventos)
      const produtosTotal = eventos.reduce((s, e) => s + e.subtotalProdutos, 0);
      const servicosTotal = eventos.reduce((s, e) => s + e.subtotalServicos, 0);
      const freteTotal = eventos.reduce((s, e) => s + e.subtotalFrete, 0);
      const totalGeral = produtosTotal + servicosTotal + freteTotal;

      // Combinar eventos duplicados (virtuais) com eventos reais do backend
      const todosCarrinhos = [...carrinhosVirtuais, ...carrinhos];
      const todosItens = [...itensVirtuais, ...allItems];
      const produtosTotalCombinado = produtosTotalDup + produtosTotal;
      const servicosTotalCombinado = servicosTotalDup + servicosTotal;
      const freteTotalCombinado = freteTotalDup + freteTotal;
      const totalCombinado = produtosTotalCombinado + servicosTotalCombinado + freteTotalCombinado;

      // Combinar nomes de produtos e serviços
      const storedProductNames = collectStoredProductNames(pedidoId);
      const allProductNames: Record<number, string> = {
        ...storedProductNames,
        ...allProductNamesDup,
      };
      const allServiceNames: Record<number, string> = { ...allServiceNamesDup };

      // Usar nomes que vêm do backend (_produto[0].Nome)
      allItems.forEach((item: any) => {
        if (
          item.produto_id &&
          item._produto &&
          item._produto[0] &&
          item._produto[0].Nome
        ) {
          allProductNames[item.produto_id] = item._produto[0].Nome;
        }
        if (item.servicos_id && item.servicos_id > 0) {
          // Para serviços, tentar obter nome do backend primeiro
          let serviceName = '';
          
          // Tentar obter do backend (_servico ou _servicos)
          if (item._servico && item._servico.Nome) {
            serviceName = item._servico.Nome;
          } else if (item._servicos && Array.isArray(item._servicos) && item._servicos[0]?.Nome) {
            serviceName = item._servicos[0].Nome;
          } else if (item._servicos && item._servicos.Nome) {
            serviceName = item._servicos.Nome;
          }
          
          // Se não encontrou no backend, usar localStorage ou nomes padrão
          if (!serviceName) {
            serviceName =
            localStorage.getItem(`serviceName_${item.servicos_id}`) ||
            (item.servicos_id === 2
                ? 'Copeira'
                : item.servicos_id === 3
              ? 'Copeira'
              : item.servicos_id === 4
                    ? 'Utensílios'
                    : item.servicos_id === 5
                ? 'Utensílios'
                : `Serviço ${item.servicos_id}`);
          }
          
          allServiceNames[item.servicos_id] = serviceName;
        }
      });

      // Salvar carrinhos combinados, todos os itens e os totais
      setCarrinhos(todosCarrinhos);
      setItems(todosItens);
      setTotal(totalCombinado);
      setFreteTotal(freteTotalCombinado);
      setServicosTotal(servicosTotalCombinado);
      setProdutosTotal(produtosTotalCombinado);
      setProductNamesById(allProductNames);
      setServiceNamesById(allServiceNames);
    } catch (error) {
      // Se houver erro ao carregar do backend, mas houver eventos duplicados, mostrar apenas eles
      if (duplicacaoAtiva && carrinhosVirtuais.length > 0) {
        setCarrinhos(carrinhosVirtuais);
        setItems(itensVirtuais);
        setProdutosTotal(produtosTotalDup);
        setServicosTotal(servicosTotalDup);
        setFreteTotal(freteTotalDup);
        setTotal(produtosTotalDup + servicosTotalDup + freteTotalDup);
        setProductNamesById({
          ...collectStoredProductNames(
            parseInt(localStorage.getItem('pedido_id') || '0') || undefined
          ),
          ...allProductNamesDup,
        });
        setServiceNamesById(allServiceNamesDup);
      } else {
        setCarrinhos([]);
        setItems([]);
        setProdutosTotal(0);
        setServicosTotal(0);
        setFreteTotal(0);
        setTotal(0);
      }
    } finally {
      isLoadingCartRef.current = false;
      if (!skipLoadingState) {
        setIsLoadingCart(false);
      }
    }
  };

  // Debounce para evitar múltiplas chamadas de loadCart
  const debouncedLoadCartRef = React.useRef<{
    timeoutId: ReturnType<typeof setTimeout> | null;
    fn: (skipLoadingState?: boolean) => void;
  }>({
    timeoutId: null,
    fn: () => {},
  });

  // Inicializar função de debounce
  React.useEffect(() => {
    debouncedLoadCartRef.current.fn = (skipLoadingState = false) => {
      if (debouncedLoadCartRef.current.timeoutId) {
        clearTimeout(debouncedLoadCartRef.current.timeoutId);
      }
      debouncedLoadCartRef.current.timeoutId = setTimeout(() => {
        loadCart(skipLoadingState);
      }, 300); // 300ms de debounce
    };
  }, []);

  const debouncedLoadCart = debouncedLoadCartRef.current.fn;

  useEffect(() => {
    if (!isOpen || items.length === 0) return;

    const missingIds = [
      ...new Set(
        items
          .filter(item => {
            if (!item?.produto_id) return false;
            if (fetchedProductNamesRef.current.has(Number(item.produto_id))) {
              return false;
            }
            const hasName =
              item._produto?.[0]?.Nome ||
              item._produto?.Nome ||
              productNamesById[item.produto_id] ||
              item.nome ||
              item.name;
            return !hasName;
          })
          .map(item => Number(item.produto_id))
          .filter(id => Number.isFinite(id) && id > 0)
      ),
    ];

    if (missingIds.length === 0) return;

    let cancelled = false;

    (async () => {
      const updates: Record<number, string> = {};

      await Promise.all(
        missingIds.map(async productId => {
          fetchedProductNamesRef.current.add(productId);
          try {
            const produto = await getJson<any>('rEsr7KLt', `produto/${productId}`);
            const model = Array.isArray(produto)
              ? produto[0]
              : produto?.items?.[0] || produto;
            const nome = model?.Nome || model?.nome;
            if (nome) updates[productId] = String(nome);
          } catch {
            // Ignorar falhas individuais de nome
          }
        })
      );

      if (!cancelled && Object.keys(updates).length > 0) {
        setProductNamesById(prev => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, items, productNamesById]);

  useEffect(() => {
    if (!isOpen) return; // Não carregar se o modal estiver fechado

    // Carregar dados do usuário em cache (uma vez só)
    fetchUserData();

    // Carregar carrinho inicial
    loadCart();

    // Recarregar quando o evento cart-updated for disparado (com debounce)
    const handleCartUpdate = () => {
      debouncedLoadCart(false);
    };

    window.addEventListener('cart-updated', handleCartUpdate);

    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, [navigate, isOpen]);

  const handleNewEvent = () => {
    try {
      // Limpar dados do fluxo atual (sem apagar pedido_id!)
      clearDuplicacaoEdicaoFormLocalStorage({ withCarrinhoId: true });

      // Marcar que estamos criando um novo evento (não editando duplicação)
      // Isso permite usar Carolino mesmo com duplicacao_ativa = true
      localStorage.setItem('novo_evento_em_criacao', 'true');

      // NÃO remover pedido_id - queremos acumular eventos no MESMO pedido!
      // NÃO remover duplicacao_ativa - queremos manter os eventos duplicados no carrinho
    } catch {}
      // Fechar o modal imediatamente (especialmente quando já estamos na Home)
      try {
        handleClose();
      } catch {}
      setStep(8);
  };

  // Função para editar evento
  const handleEditEvento = (evento: any) => {
    if (isDuplicacao) {
      const duplicacaoId = evento?.carrinho?._duplicacaoId;
      const eventosDuplicados = readDuplicacaoEventosFromStorage();
      const eventoDuplicado = eventosDuplicados.find(
        (item: any) => item?.id === duplicacaoId
      );

      if (!eventoDuplicado) {
        toast.error('Não foi possível carregar os dados deste evento.');
        return;
      }

      if (duplicacaoId) {
        localStorage.setItem(
          'duplicacao_evento_editando_id',
          String(duplicacaoId)
        );
      }

      const enderecoDuplicado = eventoDuplicado?.endereco || {};
      const produtosParaEdicao = (eventoDuplicado?.produtos || []).map(
        (produto: any) => {
          const quantity = Number(produto?.quantity || produto?.qtd || 0);
          const unitPrice = Number(
            produto?.unitPrice || produto?.price || produto?.preco || 0
          );
          return {
            ...produto,
            quantity,
            qtd: quantity,
            unitPrice,
            preco_unitario_atual: unitPrice,
            preco: unitPrice,
            price: unitPrice,
            total: produto?.total ?? roundMoney(unitPrice * quantity),
          };
        }
      );
      const servicosParaEdicao = (eventoDuplicado?.servicos || []).map(
        (servico: any) => {
          const quantity = Number(servico?.quantity || servico?.qtd || 0);
          const unitPrice = Number(
            servico?.unitPrice || servico?.price || servico?.preco || 0
          );
          return {
            ...servico,
            quantity,
            qtd: quantity,
            unitPrice,
            preco_unitario_atual: unitPrice,
            preco: unitPrice,
            price: unitPrice,
            total: servico?.total ?? roundMoney(unitPrice * quantity),
          };
        }
      );
      const selectedProducts =
        convertDuplicacaoProdutosToSelected(eventoDuplicado?.produtos) || [];
      const additionalServicesState =
        convertDuplicacaoServicosToAdditional(eventoDuplicado);

      localStorage.setItem(
        'eventType',
        eventoDuplicado?.eventSlug || (eventoDuplicado?.evento || 'manha')
      );
      localStorage.setItem(
        'pessoas',
        String(eventoDuplicado?.pessoas || 0)
      );
      localStorage.setItem('cep', eventoDuplicado?.cep || '');
      localStorage.setItem(
        'enderecoData',
        JSON.stringify({
          cep: eventoDuplicado?.cep || '',
          address: enderecoDuplicado?.logradouro || '',
          district: enderecoDuplicado?.bairro || '',
          state: enderecoDuplicado?.uf || '',
          city: enderecoDuplicado?.cidade || '',
          numero: enderecoDuplicado?.numero || '',
          complemento: enderecoDuplicado?.complemento || '',
        })
      );
      localStorage.setItem(
        'cepData',
        JSON.stringify({
          cep: eventoDuplicado?.cep || '',
          address: enderecoDuplicado?.logradouro || '',
          district: enderecoDuplicado?.bairro || '',
          state: enderecoDuplicado?.uf || '',
          city: enderecoDuplicado?.cidade || '',
          distanceKm: eventoDuplicado?.km,
        })
      );
      localStorage.setItem('distancia', eventoDuplicado?.km || '0');
      localStorage.setItem(
        'selectedProducts_manual',
        JSON.stringify(selectedProducts)
      );
      localStorage.setItem('confirmedSuggestion', 'manual');
      localStorage.setItem(
        'additionalServices',
        JSON.stringify(additionalServicesState)
      );
      localStorage.setItem('finalNotes', eventoDuplicado?.obs_logistica || '');
      localStorage.setItem(
        'finalDateTime',
        eventoDuplicado?.finalDateTime || ''
      );

      const eventoEmEdicao = {
        carrinho_id: eventoDuplicado?.localId,
        duplicacaoId: eventoDuplicado?.id,
        pedido_id: eventoDuplicado?.pedidoOriginalId,
        pessoas: eventoDuplicado?.pessoas || 0,
        cep: eventoDuplicado?.cep || '',
        endereco: enderecoDuplicado,
        produtos: produtosParaEdicao,
        servicos: servicosParaEdicao,
        cep_logistica:
          eventoDuplicado?.cep_logistica || eventoDuplicado?.cep || '',
        obs_servicos: eventoDuplicado?.obs_servicos || '',
        obs_logistica: eventoDuplicado?.obs_logistica || '',
        eventoSlug: eventoDuplicado?.eventSlug || '',
        evento: eventoDuplicado?.eventSlug || eventoDuplicado?.evento || '',
        data: eventoDuplicado?.data || '',
        hora: eventoDuplicado?.hora || '',
      };

      localStorage.setItem('evento_em_edicao', JSON.stringify(eventoEmEdicao));
      localStorage.setItem('modo_edicao_duplicacao', 'true');

      handleClose();
      setStep(1);
      navigate('/?origem=duplicacao');
      return;
    }

    // Fluxo normal (pedido atual do backend)
    const itensDoEvento = evento.itens || [];

    const eventoEmEdicao = {
      carrinho_id: evento.carrinhoId || evento.carrinho?.id,
      pedido_id: pedidoId,
      pessoas: evento.carrinho?.pessoas || 0,
      cep: evento.carrinho?.CEP || '',
      endereco: {
        logradouro: evento.carrinho?.logradouro || '',
        numero: evento.carrinho?.numero || '',
        bairro: evento.carrinho?.bairro || '',
        cidade: evento.carrinho?.cidade || '',
        UF: evento.carrinho?.UF || '',
        complemento: evento.carrinho?.complemento || '',
      },
      produtos: itensDoEvento.filter((i: any) => i.produto_id) || [],
      servicos: itensDoEvento.filter((i: any) => i.servicos_id) || [],
      cep_logistica: evento.carrinho?.CEP || '',
      obs_servicos: evento.carrinho?.obs || '',
      obs_logistica: evento.carrinho?.obs_logistica || '',
      evento: evento.carrinho?.evento || '',
      data: evento.carrinho?.data || '',
      hora: evento.carrinho?.hora || '',
    };

    localStorage.setItem('evento_em_edicao', JSON.stringify(eventoEmEdicao));
    localStorage.removeItem('modo_edicao_duplicacao');

    // Fechar checkout e navegar para Home
    handleClose();
    setStep(1);
    navigate('/');
  };

  const finalizeDuplicacaoPedido = async () => {
    const eventos = readDuplicacaoEventosFromStorage();
    if (!eventos.length) {
      // Se não há eventos duplicados, não mostrar toast (pode ter apenas eventos novos)
      return;
    }

    const todosConfirmados = eventos.every(
      (evento: any) => evento?.isConfirmed
    );

    if (!todosConfirmados) {
      const errorToastId = toast.error(
        'Confirme todos os eventos duplicados antes de finalizar o orçamento.',
        {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Fallback: forçar fechamento após 2.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(errorToastId);
      }, 2500);

      return;
    }

    // Impedir finalização se houver qualquer produto indisponível
    const itensAtuais = items;
    const temIndisponivel = itensAtuais.some(
      (it: any) => it.produto_id && !isProductAvailable(it)
    );

    if (temIndisponivel) {
      const errorToastId = toast.error(
        'Existem produtos indisponíveis neste orçamento. Substitua ou remova-os antes de finalizar.',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      setTimeout(() => {
        toast.dismiss(errorToastId);
      }, 3500);

      return;
    }

    if (!selectedPaymentMethod) {
      toast.error('Selecione uma forma de pagamento', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });
      return;
    }

    setIsFinalizing(true);
    setConfirmationEmailFailed(false);

    try {
      let userId: number | undefined;
      const userData = await fetchUserData(true);
      userId = userData?.id;

      if (!userId) {
        const localStorageUserId = localStorage.getItem('user_id');
        if (localStorageUserId) {
          userId = parseInt(localStorageUserId);
        }
      }

      // Verificar se já existe um pedido_id válido (pode ter eventos novos já criados)
      let pedidoIdParaUsar: number;
      const pedidoIdExistente = parseInt(localStorage.getItem('pedido_id') || '0');
      
      if (pedidoIdExistente && pedidoIdExistente > 0) {
        // Usar o pedido existente (que pode já ter eventos novos)
        pedidoIdParaUsar = pedidoIdExistente;
      } else {
        // Criar um novo pedido apenas se não houver um existente
        const pedidoPayload: any = {};
        if (userId) {
          pedidoPayload.user_id = userId;
        }

        const novoPedido = await postJson('rEsr7KLt', 'pedido', pedidoPayload);
        const novoPedidoId = novoPedido?.id;

        if (!novoPedidoId) {
          throw new Error('Não foi possível criar o novo pedido duplicado.');
        }
        
        pedidoIdParaUsar = novoPedidoId;
      }

      for (const evento of eventos) {
        const carolinoTracking =
          typeof evento?.isCarolino === 'boolean'
            ? carolinoTrackingFromEvento(evento)
            : getCarolinoTrackingFields(null);
        const servicos = evento?.servicos || [];
        const copeiraServico = servicos.find(
          (servico: { servico_id?: number }) => Number(servico?.servico_id) === 2
        );
        const utensiliosServico = servicos.find(
          (servico: { servico_id?: number }) => Number(servico?.servico_id) === 4
        );

        const carrinhoPayload = {
          user_id: userId || 0,
          pedido_id: pedidoIdParaUsar,
          hora: evento?.horaFinal || evento?.hora || '',
          data: evento?.dataFinal || evento?.data || '',
          evento: evento?.evento || '',
          CEP: cleanCepValue(evento?.cep_logistica || evento?.cep || ''),
          logradouro: evento?.endereco?.logradouro || '',
          numero: evento?.endereco?.numero || '',
          bairro: evento?.endereco?.bairro || '',
          cidade: evento?.endereco?.cidade || '',
          UF: evento?.endereco?.uf || '',
          pessoas: Number(evento?.pessoas || 0),
          frete: getFreteValue(evento),
          copeira: Number(copeiraServico?.quantity || 0) > 0,
          utensilios: !!utensiliosServico,
          tipo_cotacao: 'Personalizado' as const,
          complemento: evento?.endereco?.complemento || '',
          km: evento?.km || '',
          obs: evento?.obs_servicos || '',
          obs_logistica: evento?.obs_logistica || '',
          isCarolino: carolinoTracking.isCarolino,
          isBasico: carolinoTracking.isBasico,
          isEssencial: carolinoTracking.isEssencial,
          isClassico: carolinoTracking.isClassico,
          isEspecial: carolinoTracking.isEspecial,
        };

        const novoCarrinho = await createCart(carrinhoPayload);

        for (const produto of evento?.produtos || []) {
          const quantity = Number(produto?.quantity || 0);
          if (!produto?.produto_id || quantity <= 0) continue;
          const unitPrice = Number(produto?.unitPrice || produto?.price || 0);
          await addCartItem({
            produto_id: produto.produto_id,
            carrinho_id: novoCarrinho.id,
            pedido_id: pedidoIdParaUsar,
            qtd: quantity,
            total: roundMoney(unitPrice * quantity),
            user_id: userId || 0,
          });
        }

        for (const servico of evento?.servicos || []) {
          const quantity = Number(servico?.quantity || 0);
          if (!servico?.servico_id || quantity <= 0) continue;
          const unitPrice = Number(servico?.unitPrice || servico?.price || 0);
          await addCartItem({
            servicos_id: servico.servico_id,
            carrinho_id: novoCarrinho.id,
            pedido_id: pedidoIdParaUsar,
            qtd: quantity,
            total: roundMoney(unitPrice * quantity),
            user_id: userId || 0,
          });
        }
      }

      const paymentMethodForApi = selectedPaymentMethod.replace(
        /\s*\(.*\)\s*$/,
        ''
      );

      await finalizarPedido(pedidoIdParaUsar, userId, paymentMethodForApi);

      // 1.1 Criar/atualizar OP no Salesforce via backend (somente após finalizar)
      // TEMPORÁRIO: Usando endpoint alternativo sf/op_0
      try {
        await apiFetch<any>('sf/op_0', {
          versionKey: '_XyGQ1NG',
          method: 'POST',
          body: JSON.stringify({ pedidoId: String(pedidoIdParaUsar) }),
        });
      } catch (e: unknown) {
        // PETS-138: oportunidade já criada; falha só do e-mail não cancela o sucesso
        if (isOpportunityEmailFailure(e)) {
          setConfirmationEmailFailed(true);
        } else {
          throw new Error('Falha ao integrar com Salesforce (OP).');
        }
      }

      // Salvar o pedido_id para permitir gerar PDF
      localStorage.setItem('pedido_id', String(pedidoIdParaUsar));
      ultimoPedidoFinalizadoParaPdfRef.current = pedidoIdParaUsar;

      [
        'duplicacao_ativa',
        'duplicacao_eventos',
        'duplicacao_pedido_origem',
        'evento_em_edicao',
        'modo_edicao_duplicacao',
        'carrinhos_list',
        'carrinho_id',
        'selectedProducts_manual',
        'additionalServices',
        'finalDateTime',
        'finalNotes',
      ].forEach(key => localStorage.removeItem(key));

      setDuplicacaoEventos([]);
      setEventosConfirmados({});

      // Finalização visual
      window.dispatchEvent(new Event('cart-updated'));
      window.dispatchEvent(new Event('order-finalized'));

      setShowCheckoutContent(false);
      handleClose();
      toast.dismiss();
      if (toast.clearWaitingQueue) {
        toast.clearWaitingQueue();
      }
      setShowSuccessModal(true);
    } catch (error: any) {
      toast.error(
        userFacingError(error, 'Erro ao finalizar orçamento duplicado.')
      );
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleFinalizarPedido = async () => {
    // Verificar se realmente há eventos duplicados para finalizar
    if (isDuplicacao) {
      const eventosDuplicados = readDuplicacaoEventosFromStorage();
      // Se há eventos duplicados, usar o fluxo de duplicação
      if (eventosDuplicados.length > 0) {
        await finalizeDuplicacaoPedido();
        return;
      }
      // Se não há eventos duplicados, mas isDuplicacao é true (pode ter apenas eventos novos),
      // seguir o fluxo normal de finalização
    }

    // Impedir finalização se houver qualquer produto indisponível
    const temIndisponivel = items.some(
      (it: any) => it.produto_id && !isProductAvailable(it)
    );

    if (temIndisponivel) {
      const errorToastId = toast.error(
        'Existem produtos indisponíveis neste orçamento. Substitua ou remova-os antes de finalizar.',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      setTimeout(() => {
        toast.dismiss(errorToastId);
      }, 3500);

      return;
    }

    if (!pedidoId || pedidoId <= 0) {
      return;
    }

    // Validação para modo duplicação já foi tratada acima

    if (!selectedPaymentMethod) {
      toast.error('Selecione uma forma de pagamento', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });
      return;
    }

    setIsFinalizing(true);
    setConfirmationEmailFailed(false);

    try {
      const correlationId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `pf-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      finalizeCorrelationIdRef.current = correlationId;

      const carrinhoIdsCsv = carrinhos
        .map((c: any) => c?.id)
        .filter((id: any) => id != null && id !== '')
        .join(',');

      // 0. Buscar user_id usando cache (evita chamada API desnecessária)
      let userId: number | undefined;
      const userData = await fetchUserData(true); // Force refresh para garantir dados atualizados
      userId = userData?.id;

      if (!userId) {
        // Fallback: tentar localStorage
        const localStorageUserId = localStorage.getItem('user_id');
        if (localStorageUserId) {
          userId = parseInt(localStorageUserId);
        }
      }

      const paymentMethodForApi = selectedPaymentMethod.replace(/\s*\(.*\)\s*$/, '');

      void logPedidoFinalizacaoRow({
        correlation_id: correlationId,
        step: PedidoFinalizeSteps.FLOW_START,
        outcome: 'ok',
        pedido_id: pedidoId,
        carrinho_ids: carrinhoIdsCsv || null,
        user_id: userId ?? null,
        user_email_masked: maskEmail(localStorage.getItem('userEmail') || userData?.email),
        detail: null,
        items_count: items.length,
        forma_pagamento: paymentMethodForApi,
      });

      void logPedidoFinalizacaoRow({
        correlation_id: correlationId,
        step: PedidoFinalizeSteps.USER_RESOLVED,
        outcome: userId ? 'ok' : 'warn',
        pedido_id: pedidoId,
        carrinho_ids: carrinhoIdsCsv || null,
        user_id: userId ?? null,
        user_email_masked: maskEmail(localStorage.getItem('userEmail') || userData?.email),
        detail: userId ? null : 'user_id ausente após auth/me e fallback localStorage',
        items_count: items.length,
        forma_pagamento: paymentMethodForApi,
      });

      // 1. Atualizar status do pedido para FINALIZADO (com user_id e forma_pagamento se disponível)
      await finalizarPedido(pedidoId, userId, paymentMethodForApi);

      void logPedidoFinalizacaoRow({
        correlation_id: correlationId,
        step: PedidoFinalizeSteps.PEDIDO_API_OK,
        outcome: 'ok',
        pedido_id: pedidoId,
        carrinho_ids: carrinhoIdsCsv || null,
        user_id: userId ?? null,
        detail: 'pedido status FINALIZADO atualizado via API',
        items_count: items.length,
        forma_pagamento: paymentMethodForApi,
      });
      
      // Marcar que o pedido foi finalizado (para analytics)
      pedidoFinalizadoRef.current = true;
      
      // Tracking: usuário confirmou o pedido na última tela
      track('Pedido Confirmado', {
        pedido_id: pedidoId,
        forma_pagamento: paymentMethodForApi,
        total_items: items.length,
        valor_total: total,
        valor_produtos: produtosTotal,
        valor_servicos: servicosTotal,
        valor_frete: freteTotal,
      });

      // 1.1 Criar/atualizar OP no Salesforce via backend (somente após finalizar)
      // TEMPORÁRIO: Usando endpoint alternativo sf/op_0
      try {
        await apiFetch<any>('sf/op_0', {
          versionKey: '_XyGQ1NG',
          method: 'POST',
          body: JSON.stringify({ pedidoId: String(pedidoId) }),
        });
        void logPedidoFinalizacaoRow({
          correlation_id: correlationId,
          step: PedidoFinalizeSteps.SF_OP_OK,
          outcome: 'ok',
          pedido_id: pedidoId,
          carrinho_ids: carrinhoIdsCsv || null,
          user_id: userId ?? null,
          detail: 'sf/op_0 concluído',
          forma_pagamento: paymentMethodForApi,
        });
      } catch (e: unknown) {
        const emailOnlyFailure = isOpportunityEmailFailure(e);
        void logPedidoFinalizacaoRow({
          correlation_id: correlationId,
          step: emailOnlyFailure
            ? PedidoFinalizeSteps.SF_OP_EMAIL_FAIL_CONTINUED
            : PedidoFinalizeSteps.SF_OP_FAIL,
          outcome: emailOnlyFailure ? 'warn' : 'error',
          pedido_id: pedidoId,
          carrinho_ids: carrinhoIdsCsv || null,
          user_id: userId ?? null,
          detail: emailOnlyFailure
            ? `Oportunidade criada; falha restrita ao e-mail. ${safeErrorDetail(e)}`
            : safeErrorDetail(e),
          http_status: httpStatusFromError(e),
          forma_pagamento: paymentMethodForApi,
        });
        if (emailOnlyFailure) {
          setConfirmationEmailFailed(true);
        } else {
          throw new Error('Falha ao integrar com Salesforce (OP).');
        }
      }

      const pdfLogCtx: PedidoFinalizePdfLogCtx = {
        correlationId,
        pedidoId,
        carrinhoIds: carrinhoIdsCsv,
        userId,
        formaPagamento: paymentMethodForApi,
      };

      // 1.2 Gerar e enviar PDF por email (antes de limpar dados)
      try {
        await handleGeneratePDF(true, pdfLogCtx); // true = não salvar localmente, apenas gerar base64 e enviar
      } catch (pdfError: unknown) {
        void logPedidoFinalizacaoRow({
          correlation_id: correlationId,
          step: PedidoFinalizeSteps.PDF_EMAIL_WRAPPER_FAIL,
          outcome: 'error',
          pedido_id: pedidoId,
          carrinho_ids: carrinhoIdsCsv || null,
          user_id: userId ?? null,
          detail: safeErrorDetail(pdfError),
          forma_pagamento: paymentMethodForApi,
        });
        // Não bloquear o fluxo se o PDF falhar, apenas logar o erro
        console.error('Erro ao gerar/enviar PDF ao finalizar pedido:', pdfError);
      }

      void logPedidoFinalizacaoRow({
        correlation_id: correlationId,
        step: PedidoFinalizeSteps.FLOW_SUCCESS,
        outcome: 'ok',
        pedido_id: pedidoId,
        carrinho_ids: carrinhoIdsCsv || null,
        user_id: userId ?? null,
        detail:
          'Fluxo seguiu até limpeza e modal de sucesso (e-mail: ver passos EMAIL_* e PDF_*)',
        items_count: items.length,
        forma_pagamento: paymentMethodForApi,
      });
      ultimoPedidoFinalizadoParaPdfRef.current = pedidoId;

      // 2. Limpar dados do carrinho e pedido finalizado
      // Em modo duplicação, limpar apenas dados relacionados a este pedido
      if (isDuplicacao) {
        // Limpar dados específicos do pedido duplicado
        localStorage.removeItem('pedido_id');
        localStorage.removeItem('carrinho_id');
        localStorage.removeItem('carrinhos_list');
        localStorage.removeItem('duplicacao_ativa');
        localStorage.removeItem('evento_em_edicao');
        localStorage.removeItem('modo_edicao_duplicacao');
        
        // Limpar dados de produtos e serviços
        localStorage.removeItem('selectedProducts_manual');
        localStorage.removeItem('selectedProducts_carolino');
        localStorage.removeItem('selectedProducts_essencial');
        localStorage.removeItem('selectedProducts_classico');
        localStorage.removeItem('selectedProducts_basico');
        localStorage.removeItem('selectedProducts_especial');
        localStorage.removeItem('confirmedSuggestion');
        localStorage.removeItem('additionalServices');
        localStorage.removeItem('finalDateTime');
        localStorage.removeItem('finalNotes');
        
        // Limpar dados de evento
        localStorage.removeItem('eventType');
        localStorage.removeItem('pessoas');
        localStorage.removeItem('cep');
        localStorage.removeItem('cepData');
        localStorage.removeItem('enderecoData');
        localStorage.removeItem('distancia');
        
        // Limpar snapshots de nomes
        const allKeys = Object.keys(localStorage);
        for (const key of allKeys) {
          if (key.startsWith('pedidoNames_')) {
            localStorage.removeItem(key);
          }
        }
      } else {
        // Fluxo normal: limpar tudo exceto dados de login
        const keysToKeep = ['user_id', 'authToken', 'userEmail']; // Manter apenas dados de login
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
          if (!keysToKeep.includes(key)) {
            localStorage.removeItem(key);
          }
        });

        // 2.1. Garantir limpeza explícita de campos de endereço
        localStorage.removeItem('cep');
        localStorage.removeItem('cepData');
        localStorage.removeItem('enderecoData');
        localStorage.removeItem('distancia');
        localStorage.removeItem('finalDateTime');
        localStorage.removeItem('finalNotes');
      }

      // 3. Limpar cache do usuário também
      setUserDataCache(null);

      // 4. Disparar evento para atualizar header
      window.dispatchEvent(new Event('cart-updated'));
      window.dispatchEvent(new Event('order-finalized'));

      // 5. Esconder conteúdo do checkout primeiro
      setShowCheckoutContent(false);

      // 6. Fechar modal de carrinho antes de mostrar sucesso
      // Usar onClose diretamente aqui porque já marcamos pedidoFinalizadoRef.current = true
      // então handleClose não fará tracking de "fechado sem confirmar"
      onClose();

      // 7. Limpar todos os toasts antes de mostrar modal de sucesso
      toast.dismiss();
      if (toast.clearWaitingQueue) {
        toast.clearWaitingQueue();
      }
      
      // 8. Mostrar pop-up de sucesso após um pequeno delay para garantir transição suave
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 300);
    } catch (error: unknown) {
      const cid = finalizeCorrelationIdRef.current;
      if (cid) {
        void logPedidoFinalizacaoRow({
          correlation_id: cid,
          step: PedidoFinalizeSteps.FLOW_CATCH,
          outcome: 'error',
          pedido_id: pedidoId || null,
          carrinho_ids:
            carrinhos
              .map((c: any) => c?.id)
              .filter((id: any) => id != null && id !== '')
              .join(',') || null,
          user_id:
            userDataCache?.id ??
            (localStorage.getItem('user_id')
              ? parseInt(localStorage.getItem('user_id') || '0', 10)
              : null),
          user_email_masked: maskEmail(
            localStorage.getItem('userEmail') || userDataCache?.email
          ),
          detail: safeErrorDetail(error),
          http_status: httpStatusFromError(error),
        });
      }
      // Toast de erro com fallback para garantir que desapareça
      const errorFinalizeToastId = toast.error('Erro ao finalizar orçamento', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Fallback: forçar fechamento após 2.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(errorFinalizeToastId);
      }, 2500);
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleSubmitCsat = async () => {
    const nota = Math.round(csatRating);
    if (nota < 1 || nota > 5) {
      toast.info('Selecione uma nota de 1 a 5 na escala.', {
        position: 'top-right',
        autoClose: 2800,
      });
      return;
    }

    const pid =
      pedidoId > 0
        ? pedidoId
        : ultimoPedidoFinalizadoParaPdfRef.current > 0
          ? ultimoPedidoFinalizadoParaPdfRef.current
          : 0;

    if (!pid) {
      toast.error('Não foi possível identificar o orçamento para salvar a avaliação.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const comentario = csatFeedback.trim();

    try {
      const userData = userDataCache || (await fetchUserData().catch(() => null));
      const clienteNome = [userData?.first_name, userData?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      const empresaNome = String(
        userData?.company_name ||
          userData?.companyName ||
          userData?.company ||
          ''
      ).trim();

      await submitCsatResponse({
        pedido_id: pid,
        score: nota,
        comment: comentario,
        channel: 'portal-checkout',
        cliente: clienteNome || undefined,
        empresa: empresaNome || undefined,
        metadata: {
          source: 'portal-checkout',
          submitted_at: new Date().toISOString(),
          cliente: clienteNome || undefined,
          customer_name: clienteNome || undefined,
          empresa: empresaNome || undefined,
          company_name: empresaNome || undefined,
          user_id: userData?.id ?? undefined,
        },
      });
    } catch (error) {
      console.error('Erro ao salvar CSAT:', error);
      toast.error('Não foi possível registrar sua avaliação. Tente novamente.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    track('CSAT Pedido Finalizado', {
      pedido_id: pid,
      nota,
      comentario_tamanho: comentario.length,
    });
    setCsatSubmitted(true);
    toast.success('Obrigado pelo feedback!', {
      position: 'top-right',
      autoClose: 2500,
    });
  };

  const handleNovoPedido = () => {
    // Limpar todos os toasts antes de fechar e navegar
    toast.dismiss();
    if (toast.clearWaitingQueue) {
      toast.clearWaitingQueue();
    }

    ultimoPedidoFinalizadoParaPdfRef.current = 0;
    setConfirmationEmailFailed(false);

    // Limpar pedido_id e outros dados relacionados ao pedido finalizado
    localStorage.removeItem('pedido_id');
    localStorage.removeItem('carrinho_id');
    localStorage.removeItem('carrinhos_list');
    localStorage.removeItem('duplicacao_ativa');
    localStorage.removeItem('evento_em_edicao');
    localStorage.removeItem('modo_edicao_duplicacao');
    localStorage.removeItem('selectedProducts_manual');
    localStorage.removeItem('selectedProducts_carolino');
    localStorage.removeItem('additionalServices');
    localStorage.removeItem('finalDateTime');
    localStorage.removeItem('finalNotes');
    localStorage.removeItem('eventType');
    localStorage.removeItem('pessoas');
    localStorage.removeItem('cep');
    localStorage.removeItem('cepData');
    localStorage.removeItem('enderecoData');
    localStorage.removeItem('distancia');
    
    // Limpar copeira_override quando iniciar novo pedido
    clearCopeiraOverride();
    
    // Limpar best_establishment_id
    localStorage.removeItem('best_establishment_id');
    
    // Limpar react-use-cart
    localStorage.removeItem('react-use-cart');
    
    // Limpar todas as chaves pedidoNames_* (dinâmicas)
    const allKeys = Object.keys(localStorage);
    for (const key of allKeys) {
      if (key.startsWith('pedidoNames_')) {
        localStorage.removeItem(key);
      }
    }
    
    setShowSuccessModal(false);
    setShowCheckoutContent(true); // Resetar estado do checkout para próxima vez
    // Resetar o step para 1 (index/home)
    setStep(1);
    // Disparar evento para atualizar header
    window.dispatchEvent(new Event('cart-updated'));
    // Fechar o modal de checkout imediatamente
    try {
      onClose();
    } catch {}
    // Navegar para a tela inicial após fechar
    setTimeout(() => navigate('/'), 50);
  };

  const handleGeneratePDF = async (
    sendOnly: boolean = false,
    logCtx?: PedidoFinalizePdfLogCtx
  ) => {
    const pdfLog = (...args: unknown[]) =>
      console.debug('[Checkout PDF]', ...args);
    // Prevenir múltiplos cliques
    if (isGeneratingPDF) {
      pdfLog('ignorado: já gerando PDF');
      return;
    }

    pdfLog('início', {
      sendOnly,
      env: ENV,
      xanoBase: BASE_URL || '(vazio)',
      pedidoIdState: pedidoId,
      pedido_id_ls: localStorage.getItem('pedido_id'),
    });

    setIsGeneratingPDF(true);
    
    // Toast informando que está gerando
    const generatingToastId = toast.info("Gerando PDF...", {
      position: "top-right",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
    });

    try {
      if (sendOnly && logCtx) {
        void logPedidoFinalizacaoRow({
          correlation_id: logCtx.correlationId,
          step: PedidoFinalizeSteps.PDF_TRY,
          outcome: 'ok',
          pedido_id: logCtx.pedidoId,
          carrinho_ids: logCtx.carrinhoIds || null,
          user_id: logCtx.userId ?? null,
          detail: 'Início geração PDF em memória para email/pedido',
          forma_pagamento: logCtx.formaPagamento,
        });
      }

      //
      // 1. CARREGA OS DADOS
      //
      let eventosParaPDF = eventosComTotais as any[];
      let totalParaPDF = total as number;
      let pdfDataSource: 'estado' | 'api' =
        Array.isArray(eventosParaPDF) && eventosParaPDF.length > 0
          ? 'estado'
          : 'api';

      // fallback para buscar do backend
      if (!eventosParaPDF || eventosParaPDF.length === 0) {
        let pedidoIdLocal = pedidoId;
        if (!pedidoIdLocal || pedidoIdLocal <= 0) {
          pedidoIdLocal = parseInt(localStorage.getItem("pedido_id") || "0");
        }
        if ((!pedidoIdLocal || pedidoIdLocal <= 0) && carrinhos.length > 0) {
          const primeiroCarrinho = carrinhos[0];
          if (primeiroCarrinho?.pedido_id) pedidoIdLocal = primeiroCarrinho.pedido_id;
        }
        if (
          (!pedidoIdLocal || pedidoIdLocal <= 0) &&
          ultimoPedidoFinalizadoParaPdfRef.current > 0
        ) {
          pedidoIdLocal = ultimoPedidoFinalizadoParaPdfRef.current;
        }
        if (!pedidoIdLocal || pedidoIdLocal <= 0) {
          const abortCtx = {
            pedidoIdLocal,
            carrinhosLen: carrinhos.length,
            pedidoIdState: pedidoId,
            pedido_id_ls: localStorage.getItem('pedido_id'),
            ultimoPedidoFinalizadoRef: ultimoPedidoFinalizadoParaPdfRef.current,
          };
          pdfLog('abort: pedido_id não identificado após fallbacks', abortCtx);
          console.warn(
            '[Checkout PDF] pedido_id ausente após fallbacks — PDF não pode ser carregado pela API.',
            abortCtx
          );
          if (logCtx) {
            void logPedidoFinalizacaoRow({
              correlation_id: logCtx.correlationId,
              step: PedidoFinalizeSteps.PDF_ABORT_BLOCKED,
              outcome: 'error',
              pedido_id: logCtx.pedidoId,
              carrinho_ids: logCtx.carrinhoIds || null,
              user_id: logCtx.userId ?? null,
              detail: `pedido_id não encontrado após fallbacks — PDF não gerado; ctx=${JSON.stringify(abortCtx)}`,
              forma_pagamento: logCtx.formaPagamento,
            });
          }
          toast.dismiss(generatingToastId);
          toast.error("Não foi possível identificar o orçamento para gerar o PDF. Tente recarregar a página.");
          setIsGeneratingPDF(false);
          return;
        }
  
        const carrinhosResp: any[] = await getJson("rEsr7KLt", "carrinho", {
          pedido_id: pedidoIdLocal,
        });

        const itensPorCarrinho = await Promise.all(
          (carrinhosResp || []).map(async (carrinho: any) => {
            try {
              const items = await getJson("rEsr7KLt", "carrinho_produtos", {
                carrinho_id: carrinho.id,
                pedido_id: pedidoIdLocal,
              });
              const sorted = Array.isArray(items)
                ? [...items].sort((a: any, b: any) => Number(a.id) - Number(b.id))
                : [];
              return { carrinho, itens: sorted };
            } catch {
              return { carrinho, itens: [] };
            }
          })
        );

        eventosParaPDF = itensPorCarrinho.map(({ carrinho, itens }) => {
          const subtotalProdutos = itens
            .filter((i: any) => i.produto_id && !i.servicos_id)
            .reduce((s: number, it: any) => s + Number(it.total || 0), 0);
          const subtotalServicos = itens
            .filter((i: any) => i.servicos_id)
            .reduce((s: number, it: any) => s + Number(it.total || 0), 0);
          const subtotalFrete = getFreteValue(carrinho);
          const subtotal = subtotalProdutos + subtotalServicos + subtotalFrete;
          return {
            carrinho,
            itens,
            subtotal,
            subtotalProdutos,
            subtotalServicos,
            subtotalFrete,
          };
        });

        totalParaPDF = eventosParaPDF.reduce((s, e) => s + (e.subtotal || 0), 0);
        pdfLog('dados do PDF via API (fallback)', {
          pedidoIdLocal,
          eventos: eventosParaPDF.length,
          totalParaPDF,
        });
      }

      pdfLog('dados para render', {
        origem: pdfDataSource,
        eventos: eventosParaPDF.length,
        totalParaPDF,
      });

      //
      // 2. SETUP DO PDF
      //
      const pdf = new jsPDF("p", "mm", "a4");

      // Desenhar marca d'água "Prévia Orçamento" em todas as páginas
      const drawWatermark = () => {
        try {
          const pageW = (pdf as any).internal.pageSize.getWidth();
          const pageH = (pdf as any).internal.pageSize.getHeight();
          const hasG = !!(pdf as any).GState && !!(pdf as any).setGState;
          const save = (pdf as any).saveGraphicsState?.bind(pdf);
          const restore = (pdf as any).restoreGraphicsState?.bind(pdf);

        // Salvar estado gráfico para não afetar o restante do conteúdo
          if (save) save();

          if (hasG) {
            const gs = new (pdf as any).GState({ opacity: 0.1 });
            (pdf as any).setGState(gs);
          }

          // Cor vermelho mais forte; opacidade baixa vem do GState quando disponível
          pdf.setTextColor(255, 100, 100);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(90);

          // Texto centralizado no meio da página, ângulo -45°, baseline ao centro
          const opts: any = { align: "center", angle: -45 } as any;
          // baseline é suportado nas versões recentes do jsPDF
          (opts as any).baseline = 'middle';
          // Posicionar exatamente no centro da página
          const posX = pageW / 1.7;
          const posY = pageH / 3.2;
          // Desenhar "Prévia" na primeira linha (espaçamento reduzido entre as palavras)
          (pdf as any).text("PRÉVIA", posX, posY - 15, opts);
          // Desenhar "Orçamento" na segunda linha
          (pdf as any).text("ORÇAMENTO", posX, posY + 15, opts);

          // Restaurar estado para não esbranquiçar o resto do conteúdo
          if (restore) restore();
          pdf.setTextColor(0, 0, 0);
        } catch {}
      };

      // Marca d'água da primeira página
      drawWatermark();
      const PAGE_W = 210;
      const PAGE_H = 297;
  
      // 1ª página (a com moldura)
      const FP_MARGIN_X = 16;
      const FP_MARGIN_Y = 18;
      const FP_PAD = 10;
      const FP_CONTENT_W = PAGE_W - FP_MARGIN_X * 2;
      const FP_CONTENT_H = PAGE_H - FP_MARGIN_Y * 2;
      const FP_INNER_X = FP_MARGIN_X + FP_PAD;
      const FP_INNER_Y = FP_MARGIN_Y + FP_PAD;
      const FP_INNER_W = FP_CONTENT_W - FP_PAD * 2;
      const BORDER = [200, 200, 200];
      const TABLE_GRAY = [242, 242, 242];
      const TEXT = [0, 0, 0];
  
      // páginas internas
      const INNER_MARGIN = 12;
      const INNER_W = PAGE_W - INNER_MARGIN * 2;
  
      // Função auxiliar para comprimir imagem
      const compressImage = async (base64: string, maxSize: number = 300, quality: number = 0.75): Promise<string> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              let width = img.width;
              let height = img.height;
              
              if (width > maxSize || height > maxSize) {
                const ratio = Math.min(maxSize / width, maxSize / height);
                width = width * ratio;
                height = height * ratio;
              }
              
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                const compressed = canvas.toDataURL('image/jpeg', quality);
                resolve(compressed);
              } else {
                resolve(base64);
              }
            } catch {
              resolve(base64);
            }
          };
          img.onerror = () => resolve(base64);
          img.src = base64;
        });
      };

      /** Prioriza concluir o PDF/envio de e-mail: imagens podem ficar em branco. */
      const COMPRESS_IMAGE_TIMEOUT_MS = 4000;
      const LOGO_BITMAP_READY_MS = 4000;
      const ITEM_IMAGE_LOAD_TIMEOUT_MS = 4000;

      const compressImageWithTimeout = async (
        base64: string,
        maxSize: number = 300,
        quality: number = 0.75
      ): Promise<string> => {
        const p = compressImage(base64, maxSize, quality);
        const timeout = new Promise<string>((resolve) =>
          window.setTimeout(() => resolve(base64), COMPRESS_IMAGE_TIMEOUT_MS)
        );
        return await Promise.race([p, timeout]);
      };

      /**
       * Aguarda decode da imagem após atribuir src (onload, onerror ou timeout).
       * Compatível com data URL em cache (img.complete).
       */
      const awaitImageBitmapReady = (
        imgEl: HTMLImageElement,
        dataUrl: string,
        timeoutMs: number
      ): Promise<void> =>
        new Promise((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            resolve();
          };
          const timer = window.setTimeout(finish, timeoutMs);
          imgEl.onload = finish;
          imgEl.onerror = finish;
          imgEl.src = dataUrl;
          requestAnimationFrame(() => {
            try {
              if (imgEl.complete && imgEl.naturalWidth > 0) finish();
            } catch {
              /* ignore */
            }
          });
        });

      const addLogoLine = async (yStart: number, innerMargin = INNER_MARGIN): Promise<number> => {
        const textoFallbackLogo = (): number => {
          pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(18);
          pdf.text("pão e talho", PAGE_W / 2, yStart, { align: "center" });
          pdf.setFontSize(11);
          pdf.setFont("helvetica", "normal");
          pdf.text("CATERING", PAGE_W / 2, yStart + 6, { align: "center" });
          return yStart + 20 + 8;
        };
        try {
          const resp = await fetch("/assets/img/logo.png");
          const blob = await resp.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result as string);
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          const compressedBase64 = await compressImageWithTimeout(base64, 200, 0.8);
          const imgEl = new Image();
          await awaitImageBitmapReady(imgEl, compressedBase64, LOGO_BITMAP_READY_MS);
          const natWRaw = imgEl.naturalWidth || imgEl.width;
          const natHRaw = imgEl.naturalHeight || imgEl.height;
          if (!natWRaw || !natHRaw) {
            throw new Error("logo_decode_vazio");
          }
          const natW = natWRaw;
          const natH = natHRaw;
          const maxW = 70;
          const maxH = 18;
          const scale = Math.min(maxW / natW, maxH / natH);
          const w = natW * scale;
          const h = natH * scale;
          try {
            pdf.addImage(compressedBase64, "JPEG", PAGE_W / 2 - w / 2, yStart, w, h);
            pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
            pdf.setLineWidth(0.3);
            const headerY = yStart - 6;
            const headerH = h + 10;
            pdf.rect(innerMargin, headerY, INNER_W, headerH);
            const rectBottom = headerY + headerH;
            yStart = rectBottom + 2;
          } catch {
            yStart = textoFallbackLogo();
          }
          return yStart;
        } catch {
          return textoFallbackLogo();
        }
      };
  
      //
      // 3. PRIMEIRA PÁGINA
      //
      pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
      pdf.setLineWidth(0.3);
      pdf.rect(FP_MARGIN_X, FP_MARGIN_Y, FP_CONTENT_W, FP_CONTENT_H);
  
      let y = FP_INNER_Y - 3;
  
      // logo primeira página (falhas de imagem → só texto — PDF segue até o envio ao e-mail)
      try {
        const resp = await fetch("/assets/img/logo.png");
        const blob = await resp.blob();
        const base64 = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result as string);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
        const compressedBase64 = await compressImageWithTimeout(base64, 200, 0.8);
        const imgEl = new Image();
        await awaitImageBitmapReady(imgEl, compressedBase64, LOGO_BITMAP_READY_MS);
        const natWRaw = imgEl.naturalWidth || imgEl.width;
        const natHRaw = imgEl.naturalHeight || imgEl.height;
        if (!natWRaw || !natHRaw) throw new Error("logo_decode_vazio_fp");
        const natW = natWRaw;
        const natH = natHRaw;
        const maxW = 70;
        const maxH = 18;
        const scale = Math.min(maxW / natW, maxH / natH);
        const w = natW * scale;
        const h = natH * scale;
        try {
          pdf.addImage(compressedBase64, "JPEG", FP_INNER_X + (FP_INNER_W - w) / 2, y, w, h);
          pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
          pdf.setLineWidth(0.3);
          const below = y + h + 4;
          pdf.line(FP_INNER_X, below, FP_INNER_X + FP_INNER_W, below);
          y += h + 8;
        } catch {
          throw new Error("logo_firstpage_addimage");
        }
      } catch {
        pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(18);
        pdf.text("pão e talho", FP_INNER_X + FP_INNER_W / 2, y, { align: "center" });
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "normal");
        pdf.text("CATERING", FP_INNER_X + FP_INNER_W / 2, y + 6, { align: "center" });
        y += 20 + 8;
      }
  
      // remover traço abaixo da logo; manter apenas espaçamento
      y += 8;
  
      // tabela de oportunidades (exatamente como o pdf) :contentReference[oaicite:1]{index=1}
      const col1 = FP_INNER_W * 0.5;
      const col2 = FP_INNER_W * 0.25;
      const col3 = FP_INNER_W * 0.25;
      const headerH = 8;
      const rowH = 8;
      const tY = y;
      const tH = headerH + eventosParaPDF.length * rowH;
  
      // contorno
      pdf.rect(FP_INNER_X, tY, FP_INNER_W, tH);
      // colunas
      pdf.line(FP_INNER_X + col1, tY, FP_INNER_X + col1, tY + tH);
      pdf.line(FP_INNER_X + col1 + col2, tY, FP_INNER_X + col1 + col2, tY + tH);
      // header
      pdf.setFillColor(TABLE_GRAY[0], TABLE_GRAY[1], TABLE_GRAY[2]);
      pdf.rect(FP_INNER_X, tY, FP_INNER_W, headerH, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text("OPORTUNIDADE", FP_INNER_X + 4, tY + 5.5);
      pdf.text("ENTREGA", FP_INNER_X + col1 + col2 / 2, tY + 5.5, { align: "center" });
      pdf.text("TOTAL", FP_INNER_X + col1 + col2 + col3 - 4, tY + 5.5, { align: "right" });

      let rY = tY + headerH;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      eventosParaPDF.forEach((ev, idx) => {
        const evName = getEventTypeName(ev.carrinho?.evento);
        const evDate = formatDate(ev.carrinho?.data);
        pdf.text(evName, FP_INNER_X + 4, rY + 5);
        pdf.text(evDate, FP_INNER_X + col1 + col2 / 2, rY + 5, { align: "center" });
        pdf.text(formatCurrency(ev.subtotal), FP_INNER_X + col1 + col2 + col3 - 4, rY + 5, {
          align: "right",
        });
        if (idx < eventosParaPDF.length - 1) {
          pdf.line(FP_INNER_X, rY + rowH, FP_INNER_X + FP_INNER_W, rY + rowH);
        }
        rY += rowH;
      });
  
      y = rY + 2;

      // linha "Valor do orçamento"
      const budgetH = 8;
      const budgetX = FP_INNER_X + col1;
      const budgetW = col2 + col3;
      pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
      pdf.rect(budgetX, y, budgetW, budgetH);
      pdf.setFillColor(TABLE_GRAY[0], TABLE_GRAY[1], TABLE_GRAY[2]);
      pdf.rect(budgetX, y, budgetW, budgetH, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text("Valor do Orçamento", budgetX + 4, y + 5);
      pdf.setFontSize(10);
      pdf.text(formatCurrency(totalParaPDF), budgetX + budgetW - 4, y + 5, { align: "right" });
      y += budgetH + 10;

      // bloco IMPORTANTE + REGRAS (mesmo texto do pdf)
      const importantText = `Para garantir o sucesso do evento, é essencial que nossa equipe seja liberada pontualmente no momento da entrega. Isso permite cumprir os horários programados e seguir nosso planejamento logístico. Contamos com uma margem de tolerância de até 20 minutos para o início da montagem, caso ocorram imprevistos.
      
A retirada dos utensílios (cestinhas e suportes) é feita no mínimo um dia útil após o evento, durante o horário comercial. Se for necessária a retirada no mesmo dia, isso deve ser informado antes da aprovação final do orçamento e haverá cobrança adicional, devido à reprogramação logística. Em caso de perda ou quebra, será cobrado o valor correspondente ao item.

Quando não contratado o serviço de montagem e utensílios, os itens serão enviados em caixas de papel bem apresentadas e é possível que seu orçamento seja entregue por aplicativos terceirizados. Estes se limitam a entregar o orçamento na recepção do térreo do seu prédio, muitas vezes por regra do próprio condomínio e/ou aplicativo.

Caso necessário para a liberação, os dados dos nossos entregadores e copeiros poderão ser enviados no primeiro horário do dia do evento, pois é quando teremos a definição da rota logística e agenda do dia. Por favor, sinalize se essa informação deve ser enviada, caso contrário, fica entendido que não é necessário cadastro prévio.

O serviço de copeira incluso no orçamento cobre até 6 horas de trabalho. Horas extras podem ser adicionadas previamente ou solicitadas no dia, conforme disponibilidade do profissional. O valor da hora extra é de R$93,50/hora.

É necessário que a aprovação do orçamento seja realizada seguindo os seguintes critérios:
• Eventos de segunda a sexta: confirmação até às 16:00h do dia útil anterior à entrega;
• Eventos aos sábados, domingos e feriados: confirmação até às 16:00h de dois dias úteis anteriores à entrega;`;
      const rulesText = `• O horário de atendimento para alterações/cancelamento é das 08h00 às 18h00 em dias úteis;
• Solicitamos que confira seu orçamento! Não haverá alterações no dia do Evento;
• Alterações após às 16:00 do dia útil anterior à entrega, mediante disponibilidade;
• Complementos, mediante disponibilidade;
• Cancelamentos após às 18:00 do dia útil anterior e no dia da entrega: 100% do valor total;
• Finais de semana/feriados — no dia útil anterior: até 12h (50%); após 12h (100%).`;

      // se não couber dentro da moldura, joga bloco pra próxima página normal
      const FIRST_PAGE_BOTTOM = FP_MARGIN_Y + FP_CONTENT_H - FP_PAD;
      let textBoxX = FP_INNER_X;
      let textBoxW = FP_INNER_W;
  
      // estimativa rápida (pode ser simples)
      const impLines = pdf.splitTextToSize(importantText, FP_INNER_W - 12);
      const rulesLines = pdf.splitTextToSize(rulesText, FP_INNER_W - 12);
      const estimatedH = 6 + impLines.length * 3.5 + 6 + rulesLines.length * 3.5 + 12;
  
      if (y + estimatedH > FIRST_PAGE_BOTTOM) {
          pdf.addPage();
          drawWatermark();
        y = await addLogoLine(INNER_MARGIN, INNER_MARGIN);
        textBoxX = INNER_MARGIN;
        textBoxW = INNER_W;
        }
  
      // escreve blocos (alinhados com a borda do quadro de cima)
      const textPad = 4; // padding mínimo interno
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text("IMPORTANTE.", textBoxX + textPad, y);
      y += 6;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf
        .splitTextToSize(importantText, textBoxW - textPad * 2)
        .forEach((line: string) => {
          pdf.text(line, textBoxX + textPad, y);
          y += 3.5;
      });
      y += 4;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text("REGRAS DE ALTERAÇÕES E CANCELAMENTO DE ORÇAMENTOS:", textBoxX + textPad, y);
      y += 6;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf
        .splitTextToSize(rulesText, textBoxW - textPad * 2)
        .forEach((line: string) => {
          pdf.text(line, textBoxX + textPad, y);
          y += 3.5;
        });
  
      //
      // 4. EVENTOS (cada um igual ao PDF)
      //
      const userData = await fetchUserData();
      pdfLog('fetchUserData (para conteúdo do PDF)', {
        userId: userData?.id ?? null,
        hasEmail: !!userData?.email,
        person_type: userData?.person_type ?? null,
      });

      // util: altura fixa do bloco de totais
      const getTotalsHeight = (hasCopeira: boolean, hasUten: boolean) => {
        // 4 linhas fixas (produtos, frete, “Total do pedido”) + até 2 linhas de serviços
        let h = 5 * 5 + 5; // ~30
        if (hasCopeira) h += 5;
        if (hasUten) h += 5;
        return h;
      };
  
      const getDeliveryHeight = (address: string, notes: string, maxW: number) => {
        // título + 4 linhas fixas + endereço (dinâmico) + obs (dinâmico)
        let h = 4 + 6; // titulo
        h += 4 * 4; // 4 campos
        const addrLines = pdf.splitTextToSize(address || "N/A", maxW).length;
        h += addrLines * 3 + 2;
        const notesLines = pdf.splitTextToSize(notes || "", maxW).length;
        h += notesLines * 3 + 6;
        return h;
      };
  
      // tabela de produto: altura da linha é fixa
      const PRODUCT_HEADER_H = 8;
      const IMAGE_SIZE = 26;
      const ROW_H = IMAGE_SIZE + 4;
  
      // função pra desenhar header da tabela numa página
      const drawProductsHeader = (
        x: number,
        y: number,
        w: number,
        colItem: number,
        colProd: number,
        colPrice: number,
        colQty: number,
        colTotal: number,
        rowsHeight: number
      ) => {
        pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
        pdf.rect(x, y, w, PRODUCT_HEADER_H + rowsHeight);
        pdf.setFillColor(TABLE_GRAY[0], TABLE_GRAY[1], TABLE_GRAY[2]);
        pdf.rect(x, y, w, PRODUCT_HEADER_H, "F");
        // colunas
        let cx = x;
        // primeira linha vertical agora é no fim da coluna única Produto
        pdf.line(cx + colItem, y, cx + colItem, y + PRODUCT_HEADER_H + rowsHeight);
        cx += colItem;
        pdf.line(cx + colPrice, y, cx + colPrice, y + PRODUCT_HEADER_H + rowsHeight);
        cx += colPrice;
        pdf.line(cx + colQty, y, cx + colQty, y + PRODUCT_HEADER_H + rowsHeight);
        // textos
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.text("PRODUTO", x + 2, y + 5);
        // centralizar cabeçalhos das colunas numéricas
        pdf.text("PREÇO", x + colItem + colPrice / 2, y + 5, { align: "center" });
        pdf.text("QTD", x + colItem + colPrice + colQty / 2, y + 5, { align: "center" });
        pdf.text("TOTAL", x + colItem + colPrice + colQty + colTotal / 2, y + 5, { align: "center" });
      };
  
      // agora realmente itera eventos
      for (let evIndex = 0; evIndex < eventosParaPDF.length; evIndex++) {
        const ev = eventosParaPDF[evIndex];

        // nova página pra cada evento
        pdf.addPage();
        drawWatermark();
        let yEvent = await addLogoLine(INNER_MARGIN);

        // bloco de infos do evento
        const evX = INNER_MARGIN + 4;
        const usableW = INNER_W - 8;
        // Topo do container do EVENTO deve incluir também estas infos
        const containerTopYInitial = yEvent - 4; // pequeno padding antes das infos
        yEvent += 4; // margem superior extra antes da linha "Evento:"
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        const evDate = formatDate(ev.carrinho?.data);
        const evName = getEventTypeName(ev.carrinho?.evento);
        const isPJ = userData?.person_type === "PJ";
  
        pdf.text("Evento:", evX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.text(`${evDate} - ${evName}`, evX + 25, yEvent);
        yEvent += 6;

        pdf.setFont("helvetica", "bold");
        pdf.text("Cotação:", evX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.text((ev.carrinhoId || ev.carrinho?.id || "N/A").toString(), evX + 25, yEvent);
        yEvent += 6;

        pdf.setFont("helvetica", "bold");
        pdf.text("Cliente:", evX, yEvent);
        pdf.setFont("helvetica", "normal");
        const clientName =
          (userData?.first_name ? `${userData.first_name} ${userData.last_name || ""}` : "") ||
          userData?.email?.split("@")[0] ||
          "N/A";
        pdf.text(clientName, evX + 25, yEvent);
        yEvent += 6;

        pdf.setFont("helvetica", "bold");
        pdf.text("Contato:", evX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.text(userData?.phone || "N/A", evX + 25, yEvent);
        yEvent += 6;

        pdf.setFont("helvetica", "bold");
        pdf.text("E-mail:", evX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.text(userData?.email || "N/A", evX + 25, yEvent);
        yEvent += 6;

        if (isPJ) {
          pdf.setFont("helvetica", "bold");
          pdf.text("Empresa:", evX, yEvent);
          pdf.setFont("helvetica", "normal");
          pdf.text(userData?.company_name || "N/A", evX + 25, yEvent);
          yEvent += 6;
        }

        yEvent += 6; // respiro antes da tabela
  
        // TABELA DE PRODUTOS (esta é a parte que a gente faz paginar só pelos dados)
        const itens = ev.itens as any[];
        // colunas
        const colImg = usableW * 0.12; // área reservada à esquerda dentro da coluna Produto
        const colProd = usableW * 0.48; // área de texto dentro da coluna Produto
        const colItem = colImg + colProd; // coluna única: Produto (imagem + nome)
        const colPrice = usableW * 0.14;
        const colQty = usableW * 0.10;
        const colTot = usableW * 0.16;
  
        // Pré-carrega thumbnails; falha em qualquer imagem não bloqueia o PDF nem o e-mail
        const imageCache: Record<number, string | null> = {};
        try {
          await Promise.all(
            itens.map(async (i: any) => {
              const cacheKey: number = i?.produto_id
                ? Number(i.produto_id)
                : -Number(i.id);
              try {
                const produtoUrl = i?._produto?.[0]?.Imagem?.url;
                const servicoUrl =
                  (Array.isArray(i?._servicos)
                    ? i?._servicos?.[0]?.Image?.url
                    : i?._servicos?.Image?.url) ||
                  (Array.isArray(i?._servico)
                    ? i?._servico?.[0]?.Image?.url
                    : i?._servico?.Image?.url);
                const fallbackLocal = i?.produto_id
                  ? `/imagens-test/${i.produto_id}.png`
                  : null;
                const imgPath = produtoUrl || servicoUrl || fallbackLocal || '';

              // Função para converter e comprimir imagem para base64 usando tag <img>
              const loadImageAsBase64 = (src: string): Promise<string | null> => {
                return new Promise((resolve) => {
                  const timer = window.setTimeout(
                    () => resolve(null),
                    ITEM_IMAGE_LOAD_TIMEOUT_MS
                  );
                  const finish = (value: string | null) => {
                    window.clearTimeout(timer);
                    resolve(value);
                  };
                  const img = new Image();
                  img.crossOrigin = 'anonymous';
                  (img as any).referrerPolicy = 'no-referrer';

                  img.onload = () => {
                    try {
                      const canvas = document.createElement('canvas');
                      const MAX_SIZE = 300;
                      let width = img.width;
                      let height = img.height;

                      if (width > MAX_SIZE || height > MAX_SIZE) {
                        const ratio = Math.min(MAX_SIZE / width, MAX_SIZE / height);
                        width = width * ratio;
                        height = height * ratio;
                      }

                      canvas.width = width;
                      canvas.height = height;
                      const ctx = canvas.getContext('2d');
                      if (ctx) {
                        ctx.drawImage(img, 0, 0, width, height);
                        const base64 = canvas.toDataURL('image/jpeg', 0.75);
                        finish(base64);
                      } else {
                        finish(null);
                      }
                    } catch {
                      finish(null);
                    }
                  };

                  img.onerror = () => finish(null);
                  img.src = src;
                });
              };
              
              // Tentar carregar da URL remota (produto ou serviço) primeiro
              if (imgPath && imgPath !== fallbackLocal) {
                const base64 = await loadImageAsBase64(imgPath);
                if (base64) {
                  imageCache[cacheKey] = base64;
                } else if (fallbackLocal) {
                  imageCache[cacheKey] = await loadImageAsBase64(fallbackLocal);
                } else {
                  imageCache[cacheKey] = null;
                }
              } else if (fallbackLocal) {
                imageCache[cacheKey] = await loadImageAsBase64(fallbackLocal);
              } else {
                imageCache[cacheKey] = null;
              }
              } catch {
                imageCache[cacheKey] = null;
              }
            })
          );
        } catch {
          /* sem thumbnails — linhas do PDF usam placeholder cinza */
        }
  
        let itemIndex = 0;
        // Container do EVENTO (borda contínua entre páginas)
        const containerLeftX = INNER_MARGIN;
        const containerRightX = INNER_MARGIN + INNER_W;
        let segmentTopY = containerTopYInitial; // começa antes das infos
        let isFirstSegment = true;
        while (itemIndex < itens.length) {
          // espaço disponível nesta página
          const maxYThisPage = PAGE_H - 18; // um pouco de margem
          const yTableStart = yEvent;
          const spaceForRows = maxYThisPage - yTableStart - PRODUCT_HEADER_H;
          const rowsThatFit = Math.floor(spaceForRows / ROW_H);
  
          // quantos itens vou desenhar agora
          const rowsNow = Math.max(1, Math.min(rowsThatFit, itens.length - itemIndex));
  
          // desenha o header e a grade desta página
          drawProductsHeader(
            INNER_MARGIN + 4,
            yTableStart,
            usableW,
            colItem,
            colProd,
            colPrice,
            colQty,
            colTot,
            rowsNow * ROW_H
          );
  
          let yRow = yTableStart + PRODUCT_HEADER_H;
          pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);

          for (let r = 0; r < rowsNow; r++) {
            const item = itens[itemIndex];
            const baseName =
              item.produto_id
                ? productNamesById[item.produto_id] ||
                  `Produto #${item.produto_id}`
                : serviceNamesById[item.servicos_id] ||
                  `Serviço #${item.servicos_id}`;

            // Sabor em linha separada, com fonte menor
            const saborRaw =
              item._produto?.[0]?.Sabor ||
              item._produto?.[0]?.sabor ||
              item._produto?.[0]?.Descricao_Sabor ||
              item._produto?.[0]?.descricao_sabor ||
              '';
            const saborText = saborRaw ? `${String(saborRaw)}` : '';
            const unit = item.qtd > 0 ? item.total / item.qtd : item.total;
  
            // imagem - calcular tamanho dinâmico para não ultrapassar o container
            const imgMargin = 2; // margem de 2mm de cada lado
            const maxImageSize = colImg - imgMargin * 2; // tamanho máximo considerando margens
            const actualImageSize = Math.min(IMAGE_SIZE, maxImageSize); // não ultrapassar 26mm nem a coluna
            
            // Centralizar imagem horizontalmente na coluna
            const imgX = INNER_MARGIN + 4 + (colImg - actualImageSize) / 2;
            const imgY = yRow + (ROW_H - actualImageSize) / 2; // centralizar verticalmente também
            // Usar o mesmo cacheKey que foi usado para construir o cache
            const cacheKey = item.produto_id ? Number(item.produto_id) : -Number(item.id);
            const imgData = imageCache[cacheKey] || null;
            if (imgData) {
              try {
                pdf.addImage(imgData, "JPEG", imgX, imgY, actualImageSize, actualImageSize);
              } catch {
                pdf.setFillColor(240, 240, 240);
                pdf.rect(imgX, imgY, actualImageSize, actualImageSize, "F");
                pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
                pdf.rect(imgX, imgY, actualImageSize, actualImageSize);
              }
            } else {
              pdf.setFillColor(240, 240, 240);
              pdf.rect(imgX, imgY, actualImageSize, actualImageSize, "F");
              pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
              pdf.rect(imgX, imgY, actualImageSize, actualImageSize);
            }
  
            // nome + sabor (na mesma coluna do produto). Texto começa após a área da imagem
            const prodX = INNER_MARGIN + 4 + colImg + 4;
            const prodTextW = colItem - colImg - 8; // largura disponível para o texto
            const lineH = 3.2;
            const saborLineH = 2.8;

            const prodLines = pdf.splitTextToSize(
              baseName.toUpperCase(),
              prodTextW
            );
            const saborLines = saborText
              ? pdf.splitTextToSize(saborText, prodTextW)
              : [];

            const textH =
              prodLines.length * lineH +
              (saborLines.length
                ? saborLines.length * saborLineH + 1.2
                : 0);
            const startY =
              yRow + (ROW_H - textH) / 2 + lineH - 0.2; // centralizar verticalmente

            // Linha 1+: nome do produto
            pdf.setFontSize(8);
            prodLines.forEach((ln: string, li: number) => {
              pdf.text(ln, prodX, startY + li * lineH);
            });

            // Linha(s) seguinte(s): sabor (se houver), com fonte menor
            if (saborLines.length) {
              const saborStartY =
                startY + prodLines.length * lineH + 1.2; // pequeno espaçamento
              pdf.setFontSize(7);
              saborLines.forEach((ln: string, li: number) => {
                pdf.text(ln, prodX, saborStartY + li * saborLineH);
              });
              pdf.setFontSize(8); // volta para tamanho padrão para o resto da linha
            }
  
            // preço
            // centralizar valores dentro das colunas
          pdf.text(
              formatCurrency(unit),
              INNER_MARGIN + 4 + colItem + colPrice / 2,
              yRow + ROW_H / 2,
              { align: "center" }
            );
  
            // qtd
            pdf.text(
              item.qtd.toString(),
              INNER_MARGIN + 4 + colItem + colPrice + colQty / 2,
              yRow + ROW_H / 2,
              { align: "center" }
            );
  
            // total
            pdf.text(
              formatCurrency(item.total),
              INNER_MARGIN + 4 + colItem + colPrice + colQty + colTot / 2,
              yRow + ROW_H / 2,
              { align: "center" }
            );
  
            // linha horizontal
            if (r < rowsNow - 1 && itemIndex + 1 < itens.length) {
            pdf.line(
                INNER_MARGIN + 4,
                yRow + ROW_H,
                INNER_MARGIN + 4 + usableW,
                yRow + ROW_H
            );
          }

            yRow += ROW_H;
            itemIndex++;
          }
  
          // sobrou espaço na mesma página pra totais? só vamos desenhar total/entrega na ÚLTIMA iteração
          const isLastPageOfThisEvent = itemIndex >= itens.length;
          if (isLastPageOfThisEvent) {
            yEvent = yRow + 10; // fim da tabela
          } else {
            // desenhar laterais (e topo no primeiro segmento) para esta página do container
            pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
            pdf.setLineWidth(0.3);
            // não desenhar a linha superior no primeiro segmento
            const pageBottomY = PAGE_H - 18;
            pdf.line(containerLeftX, segmentTopY, containerLeftX, pageBottomY);
            pdf.line(containerRightX, segmentTopY, containerRightX, pageBottomY);

            // próxima página
            pdf.addPage();
            drawWatermark();
            // Continuidade do mesmo evento: não desenhar logo
            yEvent = INNER_MARGIN + 10;
            segmentTopY = yEvent - 4; // novo topo do segmento
            isFirstSegment = false;
          }
        }
  
        //
        // agora estamos na **última** página desse evento
        // vamos desenhar o bloco de totais + bloco de entrega
        //
        const copeiraTotal = (ev.itens as any[])
          .filter((i: any) => i.servicos_id === 2)
          .reduce((s: number, it: any) => s + Number(it.total || 0), 0);
        const utensiliosTotal = (ev.itens as any[])
          .filter((i: any) => i.servicos_id === 4)
          .reduce((s: number, it: any) => s + Number(it.total || 0), 0);
  
        const totalsH = getTotalsHeight(copeiraTotal > 0, utensiliosTotal > 0);
  
        const enderecoCompleto =
          ev.carrinho?.logradouro && ev.carrinho?.cidade
            ? `${ev.carrinho.logradouro}, ${ev.carrinho.numero || ""}, ${ev.carrinho.bairro || ""} - ${ev.carrinho.cidade} - ${ev.carrinho.UF}, CEP ${ev.carrinho.CEP || ""}`
            : "N/A";
        // Observações para entrega/logística vêm de obs_logistica
        const obsFinal =
          (ev.carrinho?.obs_logistica as any) ||
          (ev.carrinho?.obs as any) ||
          localStorage.getItem("finalNotes") ||
          "";
  
        const deliveryH = getDeliveryHeight(enderecoCompleto, obsFinal, usableW - 80);
  
        // se não couber os DOIS blocos na página atual, joga só os blocos pra próxima
        if (yEvent + totalsH + 6 + deliveryH > PAGE_H - 18) {
          // fechar lateral do segmento atual até o rodapé útil (sem linha inferior)
          pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
          pdf.setLineWidth(0.3);
          const pageBottomY = PAGE_H - 18;
          if (isFirstSegment) {
            pdf.line(containerLeftX, segmentTopY, containerRightX, segmentTopY);
          }
          pdf.line(containerLeftX, segmentTopY, containerLeftX, pageBottomY);
          pdf.line(containerRightX, segmentTopY, containerRightX, pageBottomY);

          // nova página para os blocos finais (continuidade: sem logo)
          pdf.addPage();
          drawWatermark();
          yEvent = INNER_MARGIN + 10;
          // iniciar novo segmento do container
          segmentTopY = yEvent - 4;
          isFirstSegment = false;
        }
  
        // --- totais (alinhados à esquerda, valor colado ao rótulo) ---
        const sumStartX = INNER_MARGIN + 4;
        const gapX = 3; // espaçamento mínimo entre rótulo e valor
        const LABEL_FS = 10; // títulos (negrito)
        const VALUE_FS = 9;  // dados (regular)
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);

        // linha 1
        const lbl1 = "Total de produtos:";
        const lbl1W = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl1) : 42;
        pdf.text(lbl1, sumStartX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);
        pdf.text(formatCurrency(ev.subtotalProdutos || 0), sumStartX + lbl1W + gapX, yEvent);
        yEvent += 5;

        // linha 2
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);
        const lbl2 = "Frete:";
        const lbl2W = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl2) : 14;
        pdf.text(lbl2, sumStartX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);
        pdf.text(formatCurrency(ev.subtotalFrete || 0), sumStartX + lbl2W + gapX, yEvent);
        yEvent += 5;

        // linha 3 (opcional)
        if (copeiraTotal > 0) {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(LABEL_FS);
          const lbl3 = "Copeira:";
          const lbl3W = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl3) : 22;
          pdf.text(lbl3, sumStartX, yEvent);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(VALUE_FS);
          pdf.text(formatCurrency(copeiraTotal), sumStartX + lbl3W + gapX, yEvent);
          yEvent += 5;
        }

        // linha 4 (opcional)
        if (utensiliosTotal > 0) {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(LABEL_FS);
          const lbl4 = "Utensílios:";
          const lbl4W = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl4) : 30;
          pdf.text(lbl4, sumStartX, yEvent);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(VALUE_FS);
          pdf.text(formatCurrency(utensiliosTotal), sumStartX + lbl4W + gapX, yEvent);
          yEvent += 5;
        }

        yEvent += 3;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);
        const totalLbl = "Total do orçamento:";
        const totalLblW = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(totalLbl) : 50;
        pdf.text(totalLbl, sumStartX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);
        pdf.text(formatCurrency(ev.subtotal), sumStartX + totalLblW + gapX, yEvent);
        yEvent += 12;
  
        // --- bloco entrega (sem título) ---
  
        // etiquetas e valores (colados) – tamanhos unificados
        const labelX = INNER_MARGIN + 4;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);
        let lbl = "Horário de chegada do entregador:";
        let lblW = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl) : 80;
        pdf.text(lbl, labelX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);

        const finalDateTime = localStorage.getItem("finalDateTime") || "";
        const horaEntregaLocal = finalDateTime ? finalDateTime.split(" ")[1] || "" : "";
        const horaDoEvento = (ev.carrinho?.hora as any) || horaEntregaLocal || "";

        // Regra de chegada baseada em número de pessoas
        const pessoasEvento = Number(ev.carrinho?.pessoas || localStorage.getItem("pessoas") || 0);

        const parseTimeToMinutes = (timeStr: string): number | null => {
          if (!timeStr || typeof timeStr !== "string") return null;
          const parts = timeStr.split(":");
          if (parts.length < 2) return null;
          const h = Number(parts[0]);
          const m = Number(parts[1]);
          if (Number.isNaN(h) || Number.isNaN(m)) return null;
          return h * 60 + m;
        };

        const formatMinutesToTime = (mins: number): string => {
          const total = ((mins % (24 * 60)) + 24 * 60) % (24 * 60);
          const h = Math.floor(total / 60);
          const m = total % 60;
          return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        };

        let offsetMin = 0;
        if (pessoasEvento > 0) {
          if (pessoasEvento <= 30) offsetMin = 30;
          else if (pessoasEvento <= 45) offsetMin = 45;
          else offsetMin = 60;
        }

        let horaChegada = horaDoEvento || "N/A";
        const eventMins = parseTimeToMinutes(horaDoEvento);
        if (eventMins != null && offsetMin > 0) {
          horaChegada = formatMinutesToTime(eventMins - offsetMin);
        }

        pdf.text(horaChegada || "N/A", labelX + lblW + gapX, yEvent);
        yEvent += 4;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);
        lbl = "Data de início do evento:";
        lblW = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl) : 50;
        pdf.text(lbl, labelX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);
        pdf.text(ev.carrinho?.data ? formatDate(ev.carrinho.data) : "N/A", labelX + lblW + gapX, yEvent);
        yEvent += 4;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);
        lbl = "Horário de início do evento:";
        lblW = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl) : 80;
        pdf.text(lbl, labelX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);
        pdf.text(horaDoEvento || "N/A", labelX + lblW + gapX, yEvent);
        yEvent += 4;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);
        lbl = "Local de entrega:";
        lblW = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl) : 60;
        pdf.text(lbl, labelX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);
        const addrX = labelX + lblW + gapX;
        const addrLines = pdf.splitTextToSize(enderecoCompleto, INNER_MARGIN + 4 + usableW - addrX);
        addrLines.forEach((ln: string, idx: number) => {
          pdf.text(ln, addrX, yEvent + idx * 3);
        });
        yEvent += addrLines.length * 3 + 2;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(LABEL_FS);
        lbl = "Observações:";
        lblW = (pdf as any).getTextWidth ? (pdf as any).getTextWidth(lbl) : 60;
        pdf.text(lbl, labelX, yEvent);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(VALUE_FS);
        const obsX = labelX + lblW + gapX;
        const obsLines = pdf.splitTextToSize(obsFinal, INNER_MARGIN + 4 + usableW - obsX);
        obsLines.forEach((ln: string, idx: number) => {
          pdf.text(ln, obsX, yEvent + idx * 3);
        });
        yEvent += obsLines.length * 3 + 6;

        // desenhar segmento FINAL do container (com linha inferior)
        pdf.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
        pdf.setLineWidth(0.3);
        // não desenhar a linha superior no primeiro segmento (já existe a base do cabeçalho da logo)
        pdf.line(containerLeftX, segmentTopY, containerLeftX, yEvent + 4);
        pdf.line(containerRightX, segmentTopY, containerRightX, yEvent + 4);
        pdf.line(containerLeftX, yEvent + 4, containerRightX, yEvent + 4);
      }

      if (sendOnly) {
        pdfLog('sendOnly: render jsPDF concluído, gerando base64 + email/pedido');
        const today = new Date();
        const fileName = `Orcamento_PaoEtalho_${today.toISOString().split("T")[0]}.pdf`;

        let base64: string = '';
        let base64OutputError: boolean = false;
        try {
          const dataUri =
            (pdf as any).output?.('datauristring') || pdf.output('datauristring');
          base64 = String(dataUri).split(',')[1] || '';
          pdfLog('base64 gerado', {
            chars: base64.length,
            dataUriPrefix: String(dataUri).slice(0, 32),
            vazio: !base64.length,
          });
        } catch (e: unknown) {
          base64OutputError = true;
          console.error('Erro ao gerar base64 do PDF:', e);
          pdfLog('falha output datauristring', e);
          if (logCtx) {
            void logPedidoFinalizacaoRow({
              correlation_id: logCtx.correlationId,
              step: PedidoFinalizeSteps.PDF_BASE64_EMPTY,
              outcome: 'error',
              pedido_id: logCtx.pedidoId,
              carrinho_ids: logCtx.carrinhoIds || null,
              user_id: logCtx.userId ?? null,
              detail: `Falha ao serializar PDF: ${safeErrorDetail(e)}`,
              forma_pagamento: logCtx.formaPagamento,
            });
          }
        }

        if (logCtx && base64.length > 0) {
          void logPedidoFinalizacaoRow({
            correlation_id: logCtx.correlationId,
            step: PedidoFinalizeSteps.PDF_BASE64_READY,
            outcome: 'ok',
            pedido_id: logCtx.pedidoId,
            carrinho_ids: logCtx.carrinhoIds || null,
            user_id: logCtx.userId ?? null,
            pdf_base64_length_chars: base64.length,
            detail: `Arquivo PDF: ${fileName}`,
            forma_pagamento: logCtx.formaPagamento,
          });
        }
        if (logCtx && base64.length === 0 && !base64OutputError) {
          void logPedidoFinalizacaoRow({
            correlation_id: logCtx.correlationId,
            step: PedidoFinalizeSteps.PDF_BASE64_EMPTY,
            outcome: 'warn',
            pedido_id: logCtx.pedidoId,
            carrinho_ids: logCtx.carrinhoIds || null,
            user_id: logCtx.userId ?? null,
            pdf_base64_length_chars: 0,
            detail:
              'Base64 do PDF vazio após output (Xano pode rejeitar envio)',
            forma_pagamento: logCtx.formaPagamento,
          });
        }

        let userData: any = null;
        try {
          userData = await fetchUserData();
          pdfLog('fetchUserData (antes do POST email/pedido)', {
            userId: userData?.id ?? null,
            hasEmail: !!userData?.email,
          });
        } catch (userError: unknown) {
          console.error('Erro ao buscar dados do usuário:', userError);
          pdfLog('fetchUserData falhou', userError);
          if (logCtx) {
            void logPedidoFinalizacaoRow({
              correlation_id: logCtx.correlationId,
              step: PedidoFinalizeSteps.AUTH_ME_FETCH_FAIL,
              outcome: 'warn',
              pedido_id: logCtx.pedidoId,
              carrinho_ids: logCtx.carrinhoIds || null,
              user_id: logCtx.userId ?? null,
              user_email_masked: maskEmail(localStorage.getItem('userEmail')),
              detail: `auth/me falhou: ${safeErrorDetail(userError)}`,
              forma_pagamento: logCtx.formaPagamento,
            });
          }
        }

        try {
          const userEmail = userData?.email;
          if (!userEmail) {
            throw new Error(
              'Email do usuário não encontrado. Não é possível enviar o PDF.'
            );
          }

          const nomeCliente =
            String(userData?.first_name || '')
              .replace(/[<>&"']/g, '')
              .replace(/\s+/g, ' ')
              .trim() || 'cliente';
          const numeroBruto =
            (logCtx?.pedidoId && logCtx.pedidoId > 0 ? logCtx.pedidoId : 0) ||
            (pedidoId > 0 ? pedidoId : 0) ||
            (ultimoPedidoFinalizadoParaPdfRef.current > 0
              ? ultimoPedidoFinalizadoParaPdfRef.current
              : 0) ||
            parseInt(localStorage.getItem('pedido_id') || '0', 10) ||
            0;

          const payload: {
            base64: string;
            email: string;
            nome: string;
            numero_pedido: string;
            email_comercial?: string;
          } = {
            base64,
            email: userEmail,
            nome: nomeCliente,
            numero_pedido: numeroBruto > 0 ? String(numeroBruto) : '',
          };

          // SOS-10163: em DEV o comercial recebe em teste@ em vez de cliente@
          if (ENV === 'dev') {
            payload.email_comercial = EMAIL_COMERCIAL_DEV;
          }

          pdfLog('POST email/pedido', {
            endpoint: 'email/pedido',
            emailLen: userEmail.length,
            base64Len: base64.length,
            env: ENV,
            nome: payload.nome,
            numero_pedido: payload.numero_pedido,
            email_comercial: payload.email_comercial ?? '(padrão Xano)',
          });

          const emailResult = await sendBudgetConfirmationEmail(
            {
              base64: payload.base64,
              email: payload.email,
              nome: payload.nome,
              numero_pedido: payload.numero_pedido,
            },
            {
              onAttempt: (attempt, maxAttempts) => {
                pdfLog('POST email/pedido tentativa', { attempt, maxAttempts });
                if (!logCtx) return;
                void logPedidoFinalizacaoRow({
                  correlation_id: logCtx.correlationId,
                  step: PedidoFinalizeSteps.EMAIL_POST_ATTEMPT,
                  outcome: 'ok',
                  pedido_id: logCtx.pedidoId,
                  carrinho_ids: logCtx.carrinhoIds || null,
                  user_id: logCtx.userId ?? null,
                  user_email_masked: maskEmail(userEmail),
                  pdf_base64_length_chars: base64.length,
                  detail: `Tentativa ${attempt}/${maxAttempts} de email/pedido`,
                  forma_pagamento: logCtx.formaPagamento,
                });
              },
              onAttemptFail: ({ attempt, maxAttempts, error, willRetry }) => {
                pdfLog('POST email/pedido tentativa falhou', {
                  attempt,
                  willRetry,
                });
                if (!logCtx || !willRetry) return;
                void logPedidoFinalizacaoRow({
                  correlation_id: logCtx.correlationId,
                  step: PedidoFinalizeSteps.EMAIL_POST_FAIL,
                  outcome: 'warn',
                  pedido_id: logCtx.pedidoId,
                  carrinho_ids: logCtx.carrinhoIds || null,
                  user_id: logCtx.userId ?? null,
                  user_email_masked: maskEmail(userEmail),
                  detail: `Tentativa ${attempt}/${maxAttempts} falhou; nova tentativa. ${safeErrorDetail(error)}`,
                  http_status: httpStatusFromError(error),
                  pdf_base64_length_chars: base64.length,
                  forma_pagamento: logCtx.formaPagamento,
                });
              },
            }
          );
          pdfLog('POST email/pedido OK', emailResult);
          pdfLog('sendOnly: PDF enviado por email/pedido', { fileName });
          if (sendOnly) setConfirmationEmailFailed(false);

          if (logCtx) {
            void logPedidoFinalizacaoRow({
              correlation_id: logCtx.correlationId,
              step: PedidoFinalizeSteps.EMAIL_POST_OK,
              outcome: 'ok',
              pedido_id: logCtx.pedidoId,
              carrinho_ids: logCtx.carrinhoIds || null,
              user_id: logCtx.userId ?? null,
              user_email_masked: maskEmail(userEmail),
              pdf_base64_length_chars: base64.length,
              detail: `POST email/pedido concluído na tentativa ${emailResult.attempts}`,
              forma_pagamento: logCtx.formaPagamento,
            });
          }
        } catch (sendError: unknown) {
          const statusLegacy =
            (sendError as any)?.status ?? (sendError as any)?.response?.status;
          const status = httpStatusFromError(sendError) ?? statusLegacy ?? null;
          console.error('Erro ao enviar PDF para Xano:', sendError);
          pdfLog('POST email/pedido falhou', {
            message:
              sendError instanceof Error
                ? sendError.message
                : String(sendError),
            status,
            bodySnippet:
              typeof (sendError as any)?.body === 'string'
                ? (sendError as any).body.slice(0, 200)
                : undefined,
          });
          if (!logCtx) {
            /* sem correlação (ex.: apenas download manual) */
          } else if (
            sendError instanceof Error &&
            sendError.message.includes('Email do usuário não encontrado')
          ) {
            void logPedidoFinalizacaoRow({
              correlation_id: logCtx.correlationId,
              step: PedidoFinalizeSteps.EMAIL_USER_MISSING,
              outcome: 'skip',
              pedido_id: logCtx.pedidoId,
              carrinho_ids: logCtx.carrinhoIds || null,
              user_id: logCtx.userId ?? null,
              user_email_masked: maskEmail(
                localStorage.getItem('userEmail') || userData?.email
              ),
              detail:
                'Sem e-mail em auth/me; POST email/pedido não enviado',
              pdf_base64_length_chars: base64.length,
              forma_pagamento: logCtx.formaPagamento,
            });
          } else {
            const attempts =
              sendError instanceof BudgetEmailDeliveryError
                ? sendError.attempts
                : 1;
            void logPedidoFinalizacaoRow({
              correlation_id: logCtx.correlationId,
              step: PedidoFinalizeSteps.EMAIL_POST_FAIL,
              outcome: 'error',
              pedido_id: logCtx.pedidoId,
              carrinho_ids: logCtx.carrinhoIds || null,
              user_id: logCtx.userId ?? null,
              user_email_masked: maskEmail(userData?.email),
              detail: `POST email/pedido falhou após ${attempts} tentativa(s). ${safeErrorDetail(sendError)}`,
              http_status: status,
              pdf_base64_length_chars: base64.length,
              forma_pagamento: logCtx.formaPagamento,
            });
          }
          const missingUserEmail =
            sendError instanceof Error &&
            sendError.message.includes('Email do usuário não encontrado');
          if (sendOnly && !missingUserEmail) {
            setConfirmationEmailFailed(true);
          }
        }

        toast.dismiss(generatingToastId);
        pdfLog('sendOnly: fim');
        setIsGeneratingPDF(false);
        return;
      }

      // Se sendOnly = false, salvar localmente (comportamento padrão)
      const today = new Date();
      const fileName = `Orcamento_PaoEtalho_${today.toISOString().split("T")[0]}.pdf`;
      
      // Fechar toast de "Gerando PDF..." antes de salvar
      toast.dismiss(generatingToastId);
      
      // Salvar PDF localmente usando pdf.save() diretamente
      pdf.save(fileName);
      
      // Resetar estado de geração
      setIsGeneratingPDF(false);
      
      // Toast de sucesso
      const successToastId = toast.success("PDF gerado com sucesso", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Fallback: forçar fechamento após 2.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(successToastId);
      }, 2500);
      pdfLog('PDF salvo localmente OK', { fileName });
    } catch (err: unknown) {
      console.error(err);
      pdfLog('[Checkout PDF] Erro na geração', {
        message: err instanceof Error ? err.message : String(err),
        stack:
          err instanceof Error
            ? err.stack?.split('\n').slice(0, 5).join('\n')
            : undefined,
      });
      if (logCtx) {
        void logPedidoFinalizacaoRow({
          correlation_id: logCtx.correlationId,
          step: PedidoFinalizeSteps.PDF_GENERATION_FAIL,
          outcome: 'error',
          pedido_id: logCtx.pedidoId,
          carrinho_ids: logCtx.carrinhoIds || null,
          user_id: logCtx.userId ?? null,
          detail: safeErrorDetail(err),
          forma_pagamento: logCtx.formaPagamento,
        });
      }
      
      toast.dismiss(generatingToastId);

      if (sendOnly) {
        setConfirmationEmailFailed(true);
      } else {
        const errorToastId = toast.error("Erro ao gerar PDF", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setTimeout(() => {
          toast.dismiss(errorToastId);
        }, 2500);
      }
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  

  const handleSelectPaymentMethod = (method: string) => {
    setSelectedPaymentMethod(method);
    setIsPaymentExpanded(false);
  };

  const renderPaymentLabel = (text: string) => {
    if (!text) return text;
    const match = text.match(/^(.*?)\s*(\(.*\))$/);
    if (!match) return text;
    return (
      <>
        <span>{match[1].trim()} </span>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{match[2]}</span>
      </>
    );
  };

  // Debounce por item para atualização de quantidade
  const quantityUpdateTimersRef = useRef<Map<number, any>>(new Map());
  const pendingQuantityUpdatesRef = useRef<Map<number, number>>(new Map());
  const QUANTITY_DEBOUNCE_MS = 500;

  const isUtensiliosLockedInCart = useCallback(
    (item: any, eventItems?: any[]) => {
      const eventItemsForCart =
        eventItems ??
        items.filter(cartItem => cartItem.carrinho_id === item.carrinho_id);
      return isUtensiliosItemLockedByCoffee(
        item,
        eventItemsForCart,
        productNamesById,
        serviceNamesById
      );
    },
    [items, productNamesById, serviceNamesById]
  );

  const syncPedidoCoffeeLockSnapshot = useCallback(
    (carrinhoId: number, eventItems: any[]) => {
      const pedidoIdAtual = parseInt(localStorage.getItem('pedido_id') || '0');
      if (!pedidoIdAtual || pedidoIdAtual <= 0) return;

      try {
        const namesKey = `pedidoNames_${pedidoIdAtual}`;
        const snapshot = readPedidoNamesSnapshot(pedidoIdAtual);
        const hasCoffee = eventItemsHaveCoffee(eventItems, productNamesById);

        localStorage.setItem(
          namesKey,
          JSON.stringify({
            ...snapshot,
            carrinhoCoffeeLock: {
              ...(snapshot.carrinhoCoffeeLock || {}),
              [carrinhoId]: hasCoffee,
            },
          })
        );
      } catch {
        // Ignorar falhas de persistência local
      }
    },
    [productNamesById]
  );

  const notifyUtensiliosLocked = useCallback(() => {
    const toastId = toast.info(UTENSILIOS_LOCK_REASON, {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    setTimeout(() => toast.dismiss(toastId), 3500);
  }, []);

  const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
    const currentItemForLock = items.find(item => item.id === itemId);
    if (
      currentItemForLock &&
      isUtensiliosLockedInCart(currentItemForLock)
    ) {
      if (newQuantity < 1) {
        notifyUtensiliosLocked();
        return;
      }
      if (newQuantity > 1) {
        notifyUtensiliosLocked();
        return;
      }
    }

    if (newQuantity <= 0) {
      await handleDeleteItem(itemId);
      return;
    }

    // Não entrar em loading ainda; só quando a chamada ao backend começar após o debounce

    // Buscar o item atual
    const currentItem = items.find(item => item.id === itemId);
    if (!currentItem) {
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      return;
    }

    // Calcular novo total baseado no preço unitário correto
    const unitPrice = currentItem.total / currentItem.qtd;
    const newTotal = roundMoney(unitPrice * newQuantity);

    // Salvar estado anterior para possível reversão em caso de erro
    const previousItems = [...items];
    const previousProdutosTotal = produtosTotal;
    const previousServicosTotal = servicosTotal;
    const previousFreteTotal = freteTotal;
    const previousTotal = total;

    // ATUALIZAÇÃO OTIMISTA: Atualizar UI imediatamente antes da resposta do backend
    const updatedItems = items.map(item =>
      item.id === itemId ? { ...item, qtd: newQuantity, total: newTotal } : item
    );

    setItems(updatedItems);

    // Calcular novos totais otimisticamente
    const produtosTotalLocal = updatedItems
      .filter((i: any) => i.produto_id && !i.servicos_id)
      .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
    const servicosTotalLocal = updatedItems
      .filter((i: any) => i.servicos_id)
      .reduce((s: number, i: any) => s + Number(i.total || 0), 0);

    // Buscar frete dos carrinhos (não muda com quantidade)
    const freteTotalLocal = carrinhos.reduce(
      (s, c) => s + getFreteValue(c),
      0
    );
    const totalGeralLocal =
      produtosTotalLocal + servicosTotalLocal + freteTotalLocal;

    setProdutosTotal(produtosTotalLocal);
    setServicosTotal(servicosTotalLocal);
    setFreteTotal(freteTotalLocal);
    setTotal(totalGeralLocal);

    // Guardar a quantidade pendente para uso no debounce
    pendingQuantityUpdatesRef.current.set(itemId, newQuantity);

    // Debounce: reagendar a chamada ao backend para este item
    const existingTimer = quantityUpdateTimersRef.current.get(itemId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const timerId = setTimeout(async () => {
      // Pegar a quantidade mais recente que foi acumulada durante o debounce
      const finalQuantity = pendingQuantityUpdatesRef.current.get(itemId);
      if (finalQuantity === undefined) {
        // Se não há mais quantidade pendente, cancelar
        quantityUpdateTimersRef.current.delete(itemId);
        return;
      }

      // Entrar em loading somente agora, quando a chamada real ao backend vai começar
      setLoadingItems(prev => new Set([...prev, itemId]));

      // Calcular total baseado na quantidade final acumulada
      const finalTotal = roundMoney(unitPrice * finalQuantity);

      try {
        const cleanUpdatePayload: any = {
          qtd: finalQuantity,
          total: finalTotal,
          id: itemId,
          carrinho_id: currentItem.carrinho_id,
          pedido_id: currentItem.pedido_id,
          user_id: currentItem.user_id,
        };

        if (currentItem.produto_id) {
          cleanUpdatePayload.produto_id = currentItem.produto_id;
        } else if (currentItem.servicos_id) {
          cleanUpdatePayload.servicos_id = currentItem.servicos_id;
        }

        try {
          await apiFetch(`carrinho_produtos/${itemId}`, {
            versionKey: 'rEsr7KLt',
            method: 'PUT',
            body: JSON.stringify(cleanUpdatePayload),
          });

          const successToastId = toast.success('Quantidade atualizada', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setTimeout(() => {
            toast.dismiss(successToastId);
          }, 2500);
        } catch (putError) {
          // Fallback: DELETE + POST
          await apiFetch(`carrinho_produtos/${itemId}`, {
            versionKey: 'rEsr7KLt',
            method: 'DELETE',
          });

          const newItemPayload: any = {
            carrinho_id: currentItem.carrinho_id,
            pedido_id: currentItem.pedido_id,
            qtd: finalQuantity,
            total: finalTotal,
            user_id: currentItem.user_id,
          };
          if (currentItem.produto_id) {
            newItemPayload.produto_id = currentItem.produto_id;
          } else if (currentItem.servicos_id) {
            newItemPayload.servicos_id = currentItem.servicos_id;
          }

          await apiFetch('carrinho_produtos', {
            versionKey: 'rEsr7KLt',
            method: 'POST',
            body: JSON.stringify(newItemPayload),
          });

          const successToastId2 = toast.success('Quantidade atualizada', {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setTimeout(() => {
            toast.dismiss(successToastId2);
          }, 2500);
        }

        // Não disparar 'cart-updated' nem recarregar - já atualizamos otimisticamente e enviamos ao backend
        // O evento e recarregamento acontecerão naturalmente quando necessário (ex: fechar/abrir modal)
      } catch (error) {
        // Em caso de erro, reverter para estado anterior
        setItems(previousItems);
        setProdutosTotal(previousProdutosTotal);
        setServicosTotal(previousServicosTotal);
        setFreteTotal(previousFreteTotal);
        setTotal(previousTotal);
        
        // Recarregar do backend para sincronizar
        loadCart(false);
        
        const errorQuantityToastId = toast.error('Erro ao atualizar quantidade', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setTimeout(() => {
          toast.dismiss(errorQuantityToastId);
        }, 3500);
      } finally {
        setLoadingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        quantityUpdateTimersRef.current.delete(itemId);
        pendingQuantityUpdatesRef.current.delete(itemId);
      }
    }, QUANTITY_DEBOUNCE_MS);
    quantityUpdateTimersRef.current.set(itemId, timerId);
  };

  const handleDeleteItem = async (itemId: number) => {
    const itemToDelete = items.find(item => item.id === itemId);
    if (itemToDelete && isUtensiliosLockedInCart(itemToDelete)) {
      notifyUtensiliosLocked();
      return;
    }

    // Adicionar item ao loading e marcar como deletando
    setLoadingItems(prev => new Set([...prev, itemId]));
    setDeletingItems(prev => new Set([...prev, itemId]));

    // Salvar estado anterior para possível reversão em caso de erro
    const previousItems = [...items];
    const previousProdutosTotal = produtosTotal;
    const previousServicosTotal = servicosTotal;
    const previousFreteTotal = freteTotal;
    const previousTotal = total;

    // ATUALIZAÇÃO OTIMISTA: Remover item da UI imediatamente
    const updatedItems = items.filter(item => item.id !== itemId);
    setItems(updatedItems);

    if (itemToDelete?.carrinho_id) {
      const eventItemsAfterDelete = updatedItems.filter(
        item => item.carrinho_id === itemToDelete.carrinho_id
      );
      syncPedidoCoffeeLockSnapshot(
        itemToDelete.carrinho_id,
        eventItemsAfterDelete
      );
    }

    // Se não restam itens, limpar estado e voltar
    if (updatedItems.length === 0) {
      clearStateOnEmptyCart();
      return;
    }

    // Recalcular totais otimisticamente
    const produtosTotalLocal = updatedItems
      .filter((i: any) => i.produto_id && !i.servicos_id)
      .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
    const servicosTotalLocal = updatedItems
      .filter((i: any) => i.servicos_id)
      .reduce((s: number, i: any) => s + Number(i.total || 0), 0);

    // Buscar frete dos carrinhos (não muda com deleção de item)
    const freteTotalLocal = carrinhos.reduce(
      (s, c) => s + getFreteValue(c),
      0
    );
    const totalGeralLocal =
      produtosTotalLocal + servicosTotalLocal + freteTotalLocal;

    setProdutosTotal(produtosTotalLocal);
    setServicosTotal(servicosTotalLocal);
    setFreteTotal(freteTotalLocal);
    setTotal(totalGeralLocal);

    try {
      await apiFetch(`carrinho_produtos/${itemId}`, {
        versionKey: 'rEsr7KLt',
        method: 'DELETE',
      });

      // Sucesso: mostrar feedback
      const itemRemovedToastId = toast.success('Item removido', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Fallback: forçar fechamento após 2.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(itemRemovedToastId);
      }, 2500);

      // Disparar evento para atualizar contador do carrinho no header
      window.dispatchEvent(new Event('cart-updated'));

      // Sincronizar com backend (sem mostrar loading, já atualizamos otimisticamente)
      debouncedLoadCart(true);
    } catch (error) {
      // Em caso de erro, reverter para estado anterior
      setItems(previousItems);
      setProdutosTotal(previousProdutosTotal);
      setServicosTotal(previousServicosTotal);
      setFreteTotal(previousFreteTotal);
      setTotal(previousTotal);

      // Recarregar do backend para garantir sincronização
      loadCart(false);

      // Toast de erro com fallback para garantir que desapareça
      const errorItemToastId = toast.error('Erro ao excluir item', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Fallback: forçar fechamento após 3.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(errorItemToastId);
      }, 3500);
    } finally {
      // Remover item do loading e do estado de deleção
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      setDeletingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleDeleteEventAttempt = (carrinhoId: number) => {
    // Abrir o pop-up de confirmação
    setEventToDelete(carrinhoId);
    setShowDeleteConfirmDialog(true);
  };

  const handleConfirmDeleteEvent = async () => {
    if (!eventToDelete) return;

    const carrinhoId = eventToDelete;

    try {
      setIsDeletingEvent(true);

      // Em modo duplicação, apenas remover do localStorage (eventos são virtuais)
      if (isDuplicacao) {
        const eventosDuplicados = readDuplicacaoEventosFromStorage();
        // No modo duplicação, o carrinhoId usado na UI é o ID negativo (-localId).
        const localIdFromCarrinho = Math.abs(Number(carrinhoId));
        const eventoARemover = eventosDuplicados.find((evento: any, idx: number) => {
          const evLocalId = Number(evento?.localId || idx + 1);
          return evLocalId === localIdFromCarrinho;
        });

        if (!eventoARemover) {
          toast.error('Evento não encontrado para exclusão.', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setShowDeleteConfirmDialog(false);
          setEventToDelete(null);
          setIsDeletingEvent(false);
          return;
        }

        // Remover evento do array
        const eventosAtualizados = eventosDuplicados.filter(
          (evento: any, idx: number) => {
            const evLocalId = Number(evento?.localId || idx + 1);
            return evLocalId !== localIdFromCarrinho;
          }
        );
        persistDuplicacaoEventos(eventosAtualizados);

        // Remover snapshot de edição se o evento excluído era o em edição (ou não sobrou nenhum)
        if (eventosAtualizados.length === 0) {
          clearDuplicacaoEdicaoFormLocalStorage({ withCarrinhoId: true });
        } else {
          try {
            const emStr = localStorage.getItem(STORAGE_KEYS.EVENTO_EM_EDICAO);
            if (emStr) {
              const em = JSON.parse(emStr) as {
                duplicacaoId?: unknown;
                carrinho_id?: unknown;
              };
              const removedMatches =
                (eventoARemover?.id != null &&
                  em.duplicacaoId === eventoARemover.id) ||
                (em.carrinho_id != null &&
                  Number(em.carrinho_id) === localIdFromCarrinho);
              if (removedMatches) {
                clearDuplicacaoEdicaoFormLocalStorage({ withCarrinhoId: true });
              }
            }
          } catch {
            // ignorar parse
          }
        }

        // Remover do estado de confirmação
        setEventosConfirmados(prev => {
          const next = { ...prev };
          delete next[carrinhoId];
          return next;
        });

        // Recarregar carrinho para atualizar UI
        loadCart(true);

        // Fechar o pop-up de confirmação
        setShowDeleteConfirmDialog(false);
        setEventToDelete(null);

        // Toast de sucesso
        toast.success('Evento removido', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setIsDeletingEvent(false);
        return;
      }

      // Fluxo normal: deletar do backend
      // Buscar todos os itens do evento/carrinho
      const itensDoEvento = items.filter(
        item => item.carrinho_id === carrinhoId
      );

      // Adicionar carrinho ao loading
      setLoadingItems(prev => new Set([...prev, -carrinhoId]));

      // Passo 1: Deletar todos os itens do evento
      for (const item of itensDoEvento) {
        try {
          await apiFetch(`carrinho_produtos/${item.id}`, {
            versionKey: 'rEsr7KLt',
            method: 'DELETE',
          });
        } catch (error) {
          // Erro ao deletar item
        }
      }

      // Passo 2: Deletar o carrinho
      await apiFetch(`carrinho/${carrinhoId}`, {
        versionKey: 'rEsr7KLt',
        method: 'DELETE',
      });

      // Atualizar UI imediatamente com estado limpo
      setCarrinhos(prev => prev.filter(c => c.id !== carrinhoId));
      setItems(prevItems => {
        const remaining = prevItems.filter(i => i.carrinho_id !== carrinhoId);

        // Se nenhum item restou, limpar estado e voltar para início
        if (remaining.length === 0) {
          clearStateOnEmptyCart();
          return remaining;
        }

        const produtosTotalLocal = remaining
          .filter((i: any) => i.produto_id && !i.servicos_id)
          .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
        const servicosTotalLocal = remaining
          .filter((i: any) => i.servicos_id)
          .reduce((s: number, i: any) => s + Number(i.total || 0), 0);
        const freteTotalLocal = 0; // carrinho excluído; recalculará ao sync
        setProdutosTotal(produtosTotalLocal);
        setServicosTotal(servicosTotalLocal);
        setFreteTotal(freteTotalLocal);
        setTotal(produtosTotalLocal + servicosTotalLocal + freteTotalLocal);
        return remaining;
      });

      // Recarregar carrinho (sincronização com backend)
      window.dispatchEvent(new Event('cart-updated'));

      // Fechar o pop-up de confirmação
      setShowDeleteConfirmDialog(false);
      setEventToDelete(null);

      // Toast de sucesso com fallback para garantir que desapareça
      const eventRemovedToastId = toast.success('Evento removido', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Fallback: forçar fechamento após 2.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(eventRemovedToastId);
      }, 2500);
    } catch (error) {
      // Toast de erro com fallback para garantir que desapareça
      const errorToastId = toast.error('Erro ao excluir evento', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Fallback: forçar fechamento após 3.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(errorToastId);
      }, 3500);
    } finally {
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(-carrinhoId);
        return newSet;
      });
      setIsDeletingEvent(false);
    }
  };

  const handleCancelDeleteEvent = () => {
    setShowDeleteConfirmDialog(false);
    setEventToDelete(null);
  };

  // Agrupar itens por carrinho_id (EVENTO), não por pedido_id!
  const eventosAgrupados = useMemo(() => {
    const agrupados = items.reduce(
      (acc: any, it: any) => {
        const cid = it.carrinho_id;
        if (!acc[cid]) acc[cid] = [];
        acc[cid].push(it);
        return acc;
      },
      {} as Record<number, any[]>
    );
    return agrupados;
  }, [items]);

  // Calcular totais por evento (carrinho) com dados completos
  const eventosComTotais = useMemo(() => {
    const resultado = Object.entries(eventosAgrupados).map(([cid, linhas]) => {
      const carrinhoId = parseInt(cid);
      const carrinho = carrinhos.find(c => c.id === carrinhoId);

      const subtotalProdutos = (linhas as any[])
        .filter((i: any) => i.produto_id && !i.servicos_id)
        .reduce((s, it) => s + Number(it.total || 0), 0);
      const subtotalServicos = (linhas as any[])
        .filter((i: any) => i.servicos_id)
        .reduce((s, it) => s + Number(it.total || 0), 0);
      const subtotalFrete = getFreteValue(carrinho);
      const subtotal = subtotalProdutos + subtotalServicos + subtotalFrete;

      return {
        carrinhoId,
        carrinho,
        itens: linhas,
        subtotal,
        subtotalProdutos,
        subtotalServicos,
        subtotalFrete,
      };
    });
    return resultado;
  }, [eventosAgrupados, carrinhos]);

  return (
    <>
      {/* Loading de Tela Cheia ao Finalizar Pedido */}
      {(isFinalizing || isDeletingEvent) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            cursor: 'wait',
          }}
        >
          {/* Spinner */}
          <div
            style={{
              width: '80px',
              height: '80px',
              border: '8px solid #f3f4f6',
              borderTop: '8px solid #3e400f',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '24px',
            }}
          />

          {/* Mensagem Principal */}
          <p
            style={{
              color: '#fff',
              fontSize: '20px',
              fontWeight: '600',
              textAlign: 'center',
              margin: '0 0 8px 0',
            }}
          >
            {isFinalizing ? 'Finalizando orçamento...' : 'Excluindo evento...'}
          </p>

          {/* Mensagem Secundária */}
          <p
            style={{
              color: '#d1d5db',
              fontSize: '15px',
              textAlign: 'center',
              margin: 0,
            }}
          >
            Aguarde enquanto processamos
          </p>

          {/* CSS para animação */}
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </motion.div>
      )}

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop com animação de fade */}
            <motion.div
              className='checkout-overlay'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              // Desabilitar fechamento ao clicar fora do modal
              onClick={e => e.stopPropagation()}
            />

            {/* Modal com animação de escala e fade */}
            <motion.div
              className='checkout-modal'
              style={
                !isMobile
                  ? {
                      x: '-50%',
                      y: '-50%',
                    }
                  : undefined
              }
              initial={
                !isMobile
                  ? {
                      opacity: 0,
                      scale: 0.95,
                    }
                  : {
                      opacity: 0,
                      y: '100%',
                    }
              }
              animate={
                !isMobile
                  ? {
                      opacity: 1,
                      scale: 1,
                    }
                  : {
                      opacity: 1,
                      y: 0,
                    }
              }
              exit={
                !isMobile
                  ? {
                      opacity: 0,
                      scale: 0.95,
                    }
                  : {
                      opacity: 0,
                      y: '100%',
                    }
              }
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header do Modal */}
              {showCheckoutContent && (
                <div className='checkout-header'>
                  <h2
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#333',
                      margin: 0,
                    }}
                  >
                    Revisão do Orçamento
                  </h2>
                  {!hideCloseButton && (
                    <button className='checkout-close-btn' onClick={handleClose}>
                      ×
                    </button>
                  )}
                </div>
              )}

              {/* Conteúdo Principal */}
              {showCheckoutContent && (
                <div className='checkout-content'>
                  {/* Loading overlay durante carregamento inicial */}
                  {isLoadingCart && items.length === 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        borderRadius: '12px',
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid #f3f4f6',
                            borderTop: '4px solid #3e400f',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px',
                          }}
                        />
                        <p style={{ color: '#666', fontSize: '14px' }}>
                          Carregando carrinho...
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Coluna Esquerda - Lista de Eventos */}
                  <div className='checkout-left'>
                    {items.length === 0 && !isLoadingCart ? (
                      <div
                        style={{
                          padding: '40px',
                          textAlign: 'center',
                          color: '#666',
                        }}
                      >
                        <div style={{ fontSize: '18px', marginBottom: '16px' }}>
                          Seu carrinho está vazio
                        </div>
                        <button
                          onClick={handleNewEvent}
                          style={{
                            padding: '12px 24px',
                            background: '#3e400f',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '500',
                          }}
                        >
                          Adicionar novo evento
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '16px',
                        }}
                      >
                        {eventosComTotais.map(evento => {
                          const isExpanded = expandedPedidos.has(
                            evento.carrinhoId
                          );
                          // Verificar se este evento específico é duplicado (tem _duplicacaoId)
                          const isEventoDuplicado = !!evento.carrinho?._duplicacaoId;
                          const isEventoConfirmado = eventosConfirmados[evento.carrinhoId];
                          
                          return (
                            <div
                              key={evento.carrinhoId}
                              style={{
                                background: '#f8f9fa',
                                borderRadius: '12px',
                                border: isEventoDuplicado && !isEventoConfirmado
                                  ? '2px solid #ef4444'
                                  : isEventoDuplicado && isEventoConfirmado
                                  ? '2px solid #10b981'
                                  : '1px solid #e0e0e0',
                                overflow: 'hidden',
                                backgroundColor: isEventoDuplicado && !isEventoConfirmado
                                  ? '#fef2f2'
                                  : isEventoDuplicado && isEventoConfirmado
                                  ? '#f0fdf4'
                                  : '#f8f9fa',
                              }}
                            >
                              {/* Cabeçalho da Sanfona */}
                              <div
                                onClick={() => togglePedido(evento.carrinhoId)}
                                style={{
                                  padding: '20px',
                                  cursor: 'pointer',
                                  background: isExpanded 
                                    ? (isEventoDuplicado && !isEventoConfirmado
                                        ? '#fee2e2' 
                                        : isEventoDuplicado && isEventoConfirmado
                                        ? '#dcfce7'
                                        : '#fff')
                                    : (isEventoDuplicado && !isEventoConfirmado
                                        ? '#fef2f2' 
                                        : isEventoDuplicado && isEventoConfirmado
                                        ? '#f0fdf4'
                                        : '#f8f9fa'),
                                  borderBottom: isExpanded
                                    ? '1px solid #e0e0e0'
                                    : 'none',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  position: 'relative',
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  {/* Linha 1: Data - Tipo de Evento | Total */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: '8px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        color: '#3e400f',
                                      }}
                                    >
                                      {formatDate(evento.carrinho?.data)} -{' '}
                                      {getEventTypeName(
                                        evento.carrinho?.evento
                                      )}
                                    </div>

                                    {/* TOTAL + BOTÕES (alinhados) */}
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: '18px',
                                          fontWeight: 'bold',
                                          color: '#3e400f',
                                        }}
                                      >
                                        Total: {formatCurrency(evento.subtotal)}
                                      </span>

                                      {/* Botão de editar (apenas para eventos duplicados) */}
                                      {isEventoDuplicado && (
                                        <button
                                          onClick={e => {
                                            e.stopPropagation();
                                            handleEditEvento(evento);
                                          }}
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            background: '#3e400f',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                          onMouseEnter={e => {
                                            e.currentTarget.style.background = '#5C5C42';
                                          }}
                                          onMouseLeave={e => {
                                            e.currentTarget.style.background = '#3e400f';
                                          }}
                                          title='Editar evento'
                                        >
                                          <svg
                                            width='16'
                                            height='16'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                            stroke='currentColor'
                                            strokeWidth='2'
                                          >
                                            <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
                                            <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
                                          </svg>
                                        </button>
                                      )}

                                      <button
                                        onClick={e => {
                                          e.stopPropagation();
                                          setEventToDelete(evento.carrinhoId);
                                          setShowDeleteConfirmDialog(true);
                                        }}
                                        disabled={loadingItems.has(
                                          -evento.carrinhoId
                                        )}
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '6px',
                                          border: 'none',
                                          background: '#F2F0E8',
                                          color: '#5C5C42',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s',
                                          opacity: loadingItems.has(
                                            -evento.carrinhoId
                                          )
                                            ? 0.5
                                            : 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                        onMouseEnter={e => {
                                          if (
                                            !loadingItems.has(
                                              -evento.carrinhoId
                                            )
                                          ) {
                                            e.currentTarget.style.background =
                                              '#5C5C42';
                                            e.currentTarget.style.color =
                                              '#fff';
                                          }
                                        }}
                                        onMouseLeave={e => {
                                          if (
                                            !loadingItems.has(
                                              -evento.carrinhoId
                                            )
                                          ) {
                                            e.currentTarget.style.background =
                                              '#F2F0E8';
                                            e.currentTarget.style.color =
                                              '#5C5C42';
                                          }
                                        }}
                                        title={
                                          loadingItems.has(-evento.carrinhoId)
                                            ? 'Excluindo...'
                                            : 'Excluir evento'
                                        }
                                      >
                                        {loadingItems.has(
                                          -evento.carrinhoId
                                        ) ? (
                                          <span>...</span>
                                        ) : (
                                          <svg
                                            width='16'
                                            height='16'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                            stroke='currentColor'
                                            strokeWidth='2'
                                          >
                                            <path d='M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2' />
                                            <line
                                              x1='10'
                                              y1='11'
                                              x2='10'
                                              y2='17'
                                            />
                                            <line
                                              x1='14'
                                              y1='11'
                                              x2='14'
                                              y2='17'
                                            />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Linha 2: Produtos | Serviços | Frete */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: '8px',
                                      fontSize: '14px',
                                      color: '#888',
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                      }}
                                    >
                                      <span>
                                        Produtos:{' '}
                                        {formatCurrency(
                                          evento.subtotalProdutos
                                        )}
                                      </span>
                                      {(() => {
                                        // Calcular totais separados para Copeira e Utensílios
                                        const copeiraTotal = (
                                          evento.itens as any[]
                                        )
                                          .filter(
                                            (item: any) =>
                                              item.servicos_id === 2
                                          )
                                          .reduce(
                                            (sum: number, item: any) =>
                                              sum + Number(item.total || 0),
                                            0
                                          );

                                        const utensiliosTotal = (
                                          evento.itens as any[]
                                        )
                                          .filter(
                                            (item: any) =>
                                              item.servicos_id === 4
                                          )
                                          .reduce(
                                            (sum: number, item: any) =>
                                              sum + Number(item.total || 0),
                                            0
                                          );

                                        return (
                                          <>
                                            {copeiraTotal > 0 && <span>•</span>}
                                            {copeiraTotal > 0 && (
                                              <span>
                                                Copeira:{' '}
                                                {formatCurrency(copeiraTotal)}
                                              </span>
                                            )}
                                            {utensiliosTotal > 0 && (
                                              <span>•</span>
                                            )}
                                            {utensiliosTotal > 0 && (
                                              <span>
                                                Utensílios:{' '}
                                                {formatCurrency(
                                                  utensiliosTotal
                                                )}
                                              </span>
                                            )}
                                          </>
                                        );
                                      })()}
                                      {evento.subtotalFrete != null && (
                                        <>
                                          <span>•</span>
                                          <span>
                                            Frete:{' '}
                                            {Number(evento.subtotalFrete) > 0
                                              ? formatCurrency(evento.subtotalFrete)
                                              : 'A confirmar'}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}
                                    >
                                      {/* Ícone de Expansão */}
                                      <div
                                        style={{
                                          fontSize: '16px',
                                          color: '#666',
                                          transform: isExpanded
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)',
                                          transition: 'transform 0.2s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: '24px',
                                          height: '24px',
                                        }}
                                      >
                                        <svg
                                          width='16'
                                          height='16'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                          stroke='currentColor'
                                          strokeWidth='2'
                                        >
                                          <path d='M6 9l6 6 6-6' />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Linha 3: Pessoas | CEP | Endereço */}
                                  {evento.carrinho?.pessoas && (
                                    <div
                                      style={{
                                        fontSize: '13px',
                                        color: '#888',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}
                                    >
                                      <span>
                                        {evento.carrinho.pessoas} pessoas
                                      </span>
                                      <span>•</span>
                                      <span>{evento.carrinho?.CEP || ''}</span>
                                      <span>
                                        {evento.carrinho?.logradouro || ''}{' '}
                                        {evento.carrinho?.numero || ''} -{' '}
                                        {evento.carrinho?.UF || ''}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Conteúdo Expandido */}
                              {isExpanded && (
                                <motion.div
                                  style={{
                                    padding: '20px',
                                    background: '#fff',
                                  }}
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  {/* Lista de Itens com Ações */}
                                  <div>
                                    <div
                                      style={{
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px',
                                        color: '#333',
                                      }}
                                    >
                                      Itens do Evento
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                      }}
                                    >
                                      {(evento.itens as any[])
                                        .slice()
                                        .sort(
                                          (a: any, b: any) =>
                                            Number(a.id) - Number(b.id)
                                        )
                                        .map((item: any, index: number) => {
                                          const isLoading = loadingItems.has(
                                            item.id
                                          );
                                          const isDeleting = deletingItems.has(
                                            item.id
                                          );
                                          const isAvailable =
                                            isProductAvailable(item);
                                          const isUtensiliosLocked =
                                            isUtensiliosLockedInCart(
                                              item,
                                              evento.itens as any[]
                                            );
                                          return (
                                            <motion.div
                                              key={item.id}
                                              className={`checkout-item-container ${isLoading ? 'loading' : ''} ${isDeleting ? 'deleting' : ''}`}
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: isAvailable
                                                  ? '#f8f9fa'
                                                  : '#fff5f5',
                                                border: isAvailable
                                                  ? '1px solid #e0e0e0'
                                                  : '1px solid #fecaca',
                                                borderRadius: '8px',
                                                position: 'relative',
                                                overflow: 'hidden',
                                              }}
                                              initial={{ opacity: 0, x: -20 }}
                                              animate={{
                                                opacity: isLoading ? 0.7 : 1,
                                                x: 0,
                                              }}
                                              transition={{
                                                duration: 0.3,
                                                delay: index * 0.05,
                                              }}
                                            >
                                              {/* Loading Overlay */}
                                              {isLoading && (
                                                <motion.div
                                                  className='checkout-item-loading-overlay'
                                                  initial={{ opacity: 0 }}
                                                  animate={{ opacity: 1 }}
                                                  exit={{ opacity: 0 }}
                                                  transition={{ duration: 0.2 }}
                                                >
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    <div className='checkout-item-loading-spinner' />
                                                    <div className='checkout-item-loading-text'>
                                                      {isDeleting
                                                        ? 'Removendo...'
                                                        : 'Atualizando...'}
                                                    </div>
                                                  </div>
                                                </motion.div>
                                              )}
                                              {/* Imagem do Produto */}
                                              <div
                                                style={{
                                                  width: '60px',
                                                  height: '60px',
                                                  background: '#e0e0e0',
                                                  borderRadius: '8px',
                                                  marginRight: '16px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontSize: '12px',
                                                  color: '#666',
                                                  overflow: 'hidden',
                                                }}
                                              >
                                                {(() => {
                                                  const imgSrc =
                                                    item._produto?.[0]?.Imagem?.url ||
                                                    (Array.isArray(item._servicos)
                                                      ? item._servicos?.[0]?.Image?.url
                                                      : item._servicos?.Image?.url) ||
                                                    (Array.isArray(item._servico)
                                                      ? item._servico?.[0]?.Image?.url
                                                      : item._servico?.Image?.url) ||
                                                    (item.produto_id
                                                      ? `/imagens-test/${item.produto_id}.png`
                                                      : undefined);
                                                  return imgSrc ? (
                                                    <img
                                                      src={imgSrc}
                                                      alt='Item'
                                                      style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                      }}
                                                      onError={e => {
                                                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                                                        const nextElement = e.currentTarget
                                                          .nextElementSibling as HTMLElement;
                                                        if (nextElement) {
                                                          nextElement.style.display = 'flex';
                                                        }
                                                      }}
                                                    />
                                                  ) : null;
                                                })()}
                                                <div
                                                  style={{
                                                    display: 'none',
                                                    width: '100%',
                                                    height: '100%',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                  }}
                                                >
                                                  📦
                                                </div>
                                              </div>

                                              {/* Informações do Produto */}
                                              <div style={{ flex: 1 }}>
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    marginBottom: '4px',
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontWeight: '500',
                                                    }}
                                                  >
                                                    {item.produto_id
                                                      ? productNamesById[
                                                          item.produto_id
                                                        ] ||
                                                        `Produto #${item.produto_id}`
                                                      : serviceNamesById[
                                                          item.servicos_id
                                                        ] ||
                                                        `Serviço #${item.servicos_id}`}
                                                  </div>
                                                  {!isAvailable &&
                                                    item.produto_id && (
                                                      <span
                                                        style={{
                                                          fontSize: '11px',
                                                          fontWeight: 600,
                                                          color: '#dc2626',
                                                          background: '#fee2e2',
                                                          padding: '2px 8px',
                                                          borderRadius: '4px',
                                                          textTransform:
                                                            'uppercase',
                                                        }}
                                                      >
                                                        Indisponível
                                                      </span>
                                                    )}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: '14px',
                                                    color: '#666',
                                                    marginBottom: '2px',
                                                  }}
                                                >
                                                  Un.:{' '}
                                                  {formatCurrency(
                                                    item.qtd > 0
                                                      ? Number(
                                                          item.total || 0
                                                        ) / item.qtd
                                                      : 0
                                                  )}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    color: '#3e400f',
                                                  }}
                                                >
                                                  Total:{' '}
                                                  {formatCurrency(
                                                    Number(item.total || 0)
                                                  )}
                                                </div>
                                              </div>

                                              {/* Controles de Quantidade */}
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '8px',
                                                  marginRight: '16px',
                                                }}
                                              >
                                                <button
                                                  className='checkout-quantity-btn'
                                                  onClick={() => {
                                                    handleUpdateQuantity(
                                                      item.id,
                                                      item.qtd - 1
                                                    );
                                                  }}
                                                  disabled={
                                                    loadingItems.has(item.id) ||
                                                    !isAvailable ||
                                                    (isUtensiliosLocked &&
                                                      item.qtd <= 1)
                                                  }
                                                  title={
                                                    isUtensiliosLocked &&
                                                    item.qtd <= 1
                                                      ? UTENSILIOS_LOCK_REASON
                                                      : undefined
                                                  }
                                                  onMouseEnter={e => {
                                                    if (
                                                      !loadingItems.has(item.id)
                                                    ) {
                                                      e.currentTarget.style.background =
                                                        '#5C5C42';
                                                      e.currentTarget.style.color =
                                                        '#fff';
                                                    }
                                                  }}
                                                  onMouseLeave={e => {
                                                    if (
                                                      !loadingItems.has(item.id)
                                                    ) {
                                                      e.currentTarget.style.background =
                                                        'transparent';
                                                      e.currentTarget.style.color =
                                                        '#5C5C42';
                                                    }
                                                  }}
                                                  aria-label='Diminuir'
                                                >
                                                  <svg
                                                    width='12'
                                                    height='12'
                                                    viewBox='0 0 24 24'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    strokeWidth='2'
                                                  >
                                                    <path d='M5 12h14' />
                                                  </svg>
                                                </button>
                                                <span
                                                  className={`checkout-quantity-display ${isLoading ? 'checkout-loading-pulse' : ''}`}
                                                >
                                                  {item.qtd}
                                                </span>
                                                <button
                                                  className='checkout-quantity-btn'
                                                  onClick={() => {
                                                    handleUpdateQuantity(
                                                      item.id,
                                                      item.qtd + 1
                                                    );
                                                  }}
                                                  disabled={
                                                    loadingItems.has(item.id) ||
                                                    !isAvailable ||
                                                    isUtensiliosLocked
                                                  }
                                                  title={
                                                    isUtensiliosLocked
                                                      ? UTENSILIOS_LOCK_REASON
                                                      : undefined
                                                  }
                                                  onMouseEnter={e => {
                                                    if (
                                                      !loadingItems.has(item.id)
                                                    ) {
                                                      e.currentTarget.style.background =
                                                        '#5C5C42';
                                                      e.currentTarget.style.color =
                                                        '#fff';
                                                    }
                                                  }}
                                                  onMouseLeave={e => {
                                                    if (
                                                      !loadingItems.has(item.id)
                                                    ) {
                                                      e.currentTarget.style.background =
                                                        'transparent';
                                                      e.currentTarget.style.color =
                                                        '#5C5C42';
                                                    }
                                                  }}
                                                  aria-label='Aumentar'
                                                >
                                                  <svg
                                                    width='12'
                                                    height='12'
                                                    viewBox='0 0 24 24'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    strokeWidth='2'
                                                  >
                                                    <path d='M12 5v14M5 12h14' />
                                                  </svg>
                                                </button>
                                              </div>

                                              {/* Botão de Sort (apenas para produtos indisponíveis) */}
                                              {!isAvailable && item.produto_id && (
                                                <button
                                                  onClick={() =>
                                                    handleRandomReplaceUnavailableItem(
                                                      item
                                                    )
                                                  }
                                                  style={{
                                                    background: 'transparent',
                                                    border: '1px solid #dc2626',
                                                    color: '#dc2626',
                                                    cursor: isLoading
                                                      ? 'not-allowed'
                                                      : 'pointer',
                                                    padding: '8px',
                                                    borderRadius: '6px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '36px',
                                                    height: '36px',
                                                    transition: 'all 0.2s',
                                                    opacity: isLoading ? 0.6 : 1,
                                                    marginRight: '8px',
                                                  }}
                                                  title='Substituir por item da mesma família'
                                                >
                                                  <svg
                                                    width='16'
                                                    height='16'
                                                    viewBox='0 0 24 24'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    strokeWidth='2'
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                  >
                                                    <path d='M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' />
                                                    <path d='M21 3v5h-5' />
                                                    <path d='M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' />
                                                    <path d='M3 21v-5h5' />
                                                  </svg>
                                                </button>
                                              )}

                                              {/* Botão Excluir */}
                                              <button
                                                className='checkout-delete-btn'
                                                onClick={() => {
                                                  handleDeleteItem(item.id);
                                                }}
                                                onMouseEnter={e => {
                                                  if (
                                                    !loadingItems.has(item.id)
                                                  ) {
                                                    e.currentTarget.style.background =
                                                      '#5C5C42';
                                                    e.currentTarget.style.color =
                                                      '#fff';
                                                  }
                                                }}
                                                onMouseLeave={e => {
                                                  if (
                                                    !loadingItems.has(item.id)
                                                  ) {
                                                    e.currentTarget.style.background =
                                                      '#F2F0E8';
                                                    e.currentTarget.style.color =
                                                      '#5C5C42';
                                                  }
                                                }}
                                                title={
                                                  loadingItems.has(item.id)
                                                    ? 'Processando...'
                                                    : isUtensiliosLocked
                                                      ? UTENSILIOS_LOCK_REASON
                                                      : 'Excluir item'
                                                }
                                                disabled={
                                                  loadingItems.has(item.id) ||
                                                  isUtensiliosLocked
                                                }
                                              >
                                                <svg
                                                  width='16'
                                                  height='16'
                                                  viewBox='0 0 24 24'
                                                  fill='none'
                                                  stroke='currentColor'
                                                  strokeWidth='2'
                                                >
                                                  <path d='M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2' />
                                                  <line
                                                    x1='10'
                                                    y1='11'
                                                    x2='10'
                                                    y2='17'
                                                  />
                                                  <line
                                                    x1='14'
                                                    y1='11'
                                                    x2='14'
                                                    y2='17'
                                                  />
                                                </svg>
                                              </button>
                                            </motion.div>
                                          );
                                        })}
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </div>
                          );
                        })}

                        {/* Botão Adicionar Novo Evento */}
                        <motion.button
                          onClick={handleNewEvent}
                          style={{
                            padding: '20px',
                            border: '2px dashed #3e400f',
                            background: 'transparent',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            color: '#3e400f',
                            fontWeight: '500',
                          }}
                          whileHover={{
                            scale: 1.02,
                            backgroundColor: 'rgba(62, 64, 15, 0.05)',
                          }}
                          whileTap={{ scale: 0.98 }}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 17,
                          }}
                        >
                          <span style={{ fontSize: '20px' }}>+</span>
                          Adicionar novo evento
                        </motion.button>
                      </div>
                    )}
                  </div>

                  {/* Coluna Direita - Resumo e Pagamento */}
                  <div className='checkout-right'>
                    {/* Forma de Pagamento - Sanfona */}
                    <div className='checkout-payment-section'>
                      <h3
                        style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          marginBottom: '16px',
                          color: '#3e400f',
                        }}
                      >
                        Forma de pagamento
                      </h3>

                      {/* Botão da sanfona */}
                      <button
                        className='checkout-payment-button'
                        onClick={() => setIsPaymentExpanded(!isPaymentExpanded)}
                      >
                        <span>
                          {selectedPaymentMethod
                            ? renderPaymentLabel(selectedPaymentMethod)
                            : 'Selecione'}
                        </span>
                        <div
                          style={{
                            transform: isPaymentExpanded
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                            width: '16px',
                            height: '16px',
                          }}
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                          >
                            <path d='M6 9l6 6 6-6' />
                          </svg>
                        </div>
                      </button>

                      {/* Conteúdo da sanfona */}
                      <div
                        style={{
                          overflow: 'hidden',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          maxHeight: isPaymentExpanded ? '300px' : '0px',
                          opacity: isPaymentExpanded ? 1 : 0,
                          transform: isPaymentExpanded
                            ? 'translateY(0)'
                            : 'translateY(-10px)',
                        }}
                      >
                        <div className='checkout-payment-options'>
                          {[
                            'PIX',
                            'Cartão - LINK',
                            'Faturado (empresas cadastradas)',
                          ]
                            .filter(method => method !== selectedPaymentMethod)
                            .map(method => (
                              <button
                                key={method}
                                className='checkout-payment-option'
                                onClick={() =>
                                  handleSelectPaymentMethod(method)
                                }
                                onMouseEnter={e => {
                                  if (selectedPaymentMethod !== method) {
                                    e.currentTarget.style.borderColor =
                                      '#3e400f';
                                    e.currentTarget.style.backgroundColor =
                                      '#f5f1eb';
                                    e.currentTarget.style.transform =
                                      'translateY(-2px)';
                                    e.currentTarget.style.boxShadow =
                                      '0 4px 12px rgba(62, 64, 15, 0.15)';
                                  }
                                }}
                                onMouseLeave={e => {
                                  if (selectedPaymentMethod !== method) {
                                    e.currentTarget.style.borderColor =
                                      '#e0e0e0';
                                    e.currentTarget.style.backgroundColor =
                                      '#fff';
                                    e.currentTarget.style.transform =
                                      'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }
                                }}
                              >
                                {selectedPaymentMethod === method && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      right: '12px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '50%',
                                      backgroundColor: '#3e400f',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <svg
                                      width='12'
                                      height='12'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='white'
                                      strokeWidth='3'
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                    >
                                      <path d='M20 6L9 17l-5-5' />
                                    </svg>
                                  </div>
                                )}
                                {renderPaymentLabel(method)}
                              </button>
                            ))}
                        </div>
                      </div>
                    </div>

                    {/* Resumo do Carrinho */}
                    <div className='checkout-summary'>
                      <h3
                        style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          marginBottom: '16px',
                          color: '#3e400f',
                        }}
                      >
                        Resumo do Carrinho
                      </h3>

                      <div style={{ marginBottom: '16px' }}>
                        <div className='checkout-summary-item'>
                          <span>Produtos:</span>
                          <span
                            style={{ fontWeight: 'bold', color: '#3e400f' }}
                          >
                            {formatCurrency(produtosTotal)}
                          </span>
                        </div>
                        <div className='checkout-summary-item'>
                          <span>Frete:</span>
                          <span
                            style={{ fontWeight: 'bold', color: '#3e400f' }}
                          >
                            {Number(freteTotal) > 0
                              ? formatCurrency(freteTotal)
                              : 'A confirmar'}
                          </span>
                        </div>

                        {/* Detalhes dos Serviços */}
                        {(() => {
                          // Agrupar serviços por servicos_id e calcular totais
                          const servicosMap = new Map<number, number>();
                          
                          items
                            .filter((item: any) => item.servicos_id && item.servicos_id > 0)
                            .forEach((item: any) => {
                              const serviceId = item.servicos_id;
                              const currentTotal = servicosMap.get(serviceId) || 0;
                              servicosMap.set(serviceId, currentTotal + Number(item.total || 0));
                            });

                          // Se não há serviços, não exibir nada
                          if (servicosMap.size === 0) {
                            return null;
                          }

                          // Ordenar serviços por ID para exibição consistente
                          const servicosArray = Array.from(servicosMap.entries())
                            .sort((a, b) => a[0] - b[0])
                            .map(([serviceId, total]) => ({
                              serviceId,
                              total,
                              name: serviceNamesById[serviceId] || `Serviço ${serviceId}`,
                            }));

                          return (
                            <div style={{ marginBottom: '8px' }}>
                              {servicosArray.map((servico) => (
                              <div
                                  key={servico.serviceId}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginBottom: '4px',
                                }}
                              >
                                  <span>{servico.name}:</span>
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    color: '#3e400f',
                                  }}
                                >
                                    {formatCurrency(servico.total)}
                                </span>
                              </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      <div
                        style={{
                          marginTop: '16px',
                        }}
                      >
                        <div className='checkout-summary-total'>
                          <span>Total:</span>
                          <span>{formatCurrency(total)}</span>
                        </div>

                        {/* Botão Finalizar Pedido */}
                        <motion.button
                          className='checkout-finalize-btn'
                          onClick={handleFinalizarPedido}
                          disabled={isFinalizing || items.length === 0}
                          whileHover={
                            !isFinalizing
                              ? {
                                  scale: 1.02,
                                  y: -2,
                                  boxShadow: '0 8px 20px rgba(62, 64, 15, 0.3)',
                                }
                              : {}
                          }
                          whileTap={!isFinalizing ? { scale: 0.98 } : {}}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 17,
                          }}
                        >
                          {isFinalizing
                            ? 'Finalizando...'
                            : 'Finalizar Orçamento'}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Dialog de Confirmação de Exclusão */}
            {showDeleteConfirmDialog && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10000,
                }}
                onClick={handleCancelDeleteEvent}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={e => e.stopPropagation()}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: '24px',
                    minWidth: '400px',
                    maxWidth: '90%',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 12px 0',
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: 'rgba(62, 64, 15, 1)',
                    }}
                  >
                    Excluir evento?
                  </h3>
                  <p
                    style={{
                      margin: '0 0 24px 0',
                      fontSize: '0.95rem',
                      color: '#6b7280',
                      lineHeight: '1.5',
                    }}
                  >
                    Tem certeza que deseja excluir este evento? Todos os itens
                    serão removidos.
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <button
                      onClick={handleCancelDeleteEvent}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        color: '#374151',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleConfirmDeleteEvent}
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: '#dc2626',
                        color: '#fff',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#b91c1c';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                      }}
                    >
                      Excluir
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>

      {/* Modal de Sucesso com animação - Fora do AnimatePresence para aparecer mesmo com modal fechado */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '40px',
                maxWidth: '560px',
                width: '90%',
                maxHeight: '92vh',
                overflowY: 'auto',
                textAlign: 'center',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
              }}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                duration: 0.3,
                delay: 0.1,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <CheckoutSuccessPanel
                isGeneratingPDF={isGeneratingPDF}
                onNovoPedido={handleNovoPedido}
                onGerarPdf={() => handleGeneratePDF(false)}
                csatRating={csatRating}
                onCsatRatingChange={setCsatRating}
                csatFeedback={csatFeedback}
                onCsatFeedbackChange={setCsatFeedback}
                csatSubmitted={csatSubmitted}
                onSubmitCsat={handleSubmitCsat}
                confirmationEmailFailed={confirmationEmailFailed}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Componente wrapper para manter compatibilidade com a rota
export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    setIsOpen(false);
    // Pequeno delay para permitir animação de saída
    setTimeout(() => navigate('/'), 300);
  };

  return (
    <CheckoutModal
      isOpen={isOpen}
      onClose={handleClose}
      hideCloseButton={true}
    />
  );
};

export const CheckoutSuccessPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => navigate('/'), 300);
  };

  return (
    <CheckoutModal
      isOpen={isOpen}
      onClose={handleClose}
      hideCloseButton={false}
      previewFinalSuccess={true}
    />
  );
};

export default CheckoutModal;
